import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
  LineChart, Line, PieChart, Pie, Cell, ReferenceLine
} from 'recharts';
import {
  Clock, CheckCircle, Package, Database, RefreshCw, FileText, AlertTriangle,
  Calendar, TrendingUp, LayoutGrid, Tag, Gauge, ListChecks, RotateCcw, Users,
  BarChart3, KeyRound, History, ExternalLink, Mail, Truck, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import FinalizarDiaDialog from '../components/rodizio/FinalizarDiaDialog';
import AcessosCanais from '../components/dashboard/AcessosCanais';
import ListaCobranca from '../components/dashboard/ListaCobranca';
import HistoricoVerificacao from '../components/dashboard/HistoricoVerificacao';
import CheckCanalDialog, { RespostasCanal, resumoDoCheck } from '../components/dashboard/CheckCanal';
import ManualAtividade from '../components/dashboard/ManualAtividade';
import NovidadesDashboard from '../components/dashboard/NovidadesDashboard';
import FalhaComprasFornecedor from '../components/dashboard/FalhaComprasFornecedor';
import CancelamentosPeriodo from '../components/dashboard/CancelamentosPeriodo';
import AbaKpis from '../components/dashboard/AbaKpis';
import BotaoListaLL from '../components/dashboard/BotaoListaLL';
import BotaoListaCSU from '../components/dashboard/BotaoListaCSU';
import MetaHistorico from '../components/dashboard/MetaHistorico';
import { textoColunaDemora } from '../components/atendimento/cobrancaCanal';
import { linhaDaCaixa, COR_DA_LINHA } from '../components/dashboard/linhaMeta';
import { corPessoa, rotinaDe, proximoAcionamento, baixarBlob, linkDoPortal, qtdAtividade, TELA_PEND_TRANSP, EMAIL_ATENDIMENTO, PLANILHA_CANCELAMENTO_GERAL } from '../components/dashboard/rotina';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

// Atividades da "Verificação de atividades": TODAS diárias em dias úteis (decisão da Adnéia,
// 06/10/2026). Mesma lista do _ATIV_LABELS do backend (routes/dashboard.py): atividade nova
// entra nos DOIS. Antes, a que faltava numa lista de nomes caía no padrão terça/quinta da
// Verificação de status — Tem em Casa, Pendência de Transportadora, Ocorrência GRS e
// Atendimento caíram assim — e o AES tinha seg/sex própria.
const ATIVIDADES_DIARIAS = new Set([
  'tem em casa', 'disp. p/ retirada', 'pag. não aprovado', 'cancelamentos (aes)',
  'falta na produção (etr)', 'etr (produção)', 'erro na nota',
  'pendência de transportadora', 'ocorrência grs', 'atendimento',
]);

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('visao-geral');
  const [periodoDias, setPeriodoDias] = useState(30);
  const [canalFiltro, setCanalFiltro] = useState('');
  const [fornecedorFiltro, setFornecedorFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Finalizar Dia: a janela (components/rodizio/FinalizarDiaDialog.js) mostra o que
  // ficou aberto do rodízio da pessoa antes de confirmar.
  const [showFinalizarDialog, setShowFinalizarDialog] = useState(false);

  // Data states
  const [visaoGeral, setVisaoGeral] = useState(null);
  const [volumeCanal, setVolumeCanal] = useState(null);
  const [classificacao, setClassificacao] = useState(null);
  const [fcAbertos, setFcAbertos] = useState({});   // Falha Compras por fornecedor: quais "+" estão abertos
  const [performance, setPerformance] = useState(null);
  const [pendencias, setPendencias] = useState(null);
  const [estornos, setEstornos] = useState(null);
  const [reincidencia, setReincidencia] = useState(null);
  const [filtros, setFiltros] = useState({ canais: [], fornecedores: [] });
  const [expandedMonths, setExpandedMonths] = useState({});
  const toggleMonth = (month) => setExpandedMonths(prev => ({ ...prev, [month]: !prev[month] }));

  // Atividades (Retirada + Pagamento + Cancelamentos)
  const [retiradaData, setRetiradaData] = useState(null);
  const [pagamentoData, setPagamentoData] = useState(null);
  const [cancelamentosData, setCancelamentosData] = useState(null);
  // A meta de bonificação aberta no gráfico do mês (Adnéia, 30/10/2026). Fica aqui, e não na
  // aba Performance, porque a aba é remontada a cada render.
  const [metaAberta, setMetaAberta] = useState(null);
  const [loadingAtividades, setLoadingAtividades] = useState(false);

  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  // Aba Acessos: só o atendimento (admin e atendente; os perfis dashboard e consulta, não).
  // O backend confere de novo (decisão 3, 17/10/2026).
  const veAcessos = !user?.role || ['admin', 'atendente'].includes(user.role);

  // Grafico "Atendimentos por Mes" — filtro de periodo (endpoint dedicado; fallback p/ visaoGeral)
  const [atendMes, setAtendMes] = useState(null);
  const [mesIni, setMesIni] = useState('');
  const [mesFim, setMesFim] = useState('');
  const fetchAtendMes = useCallback(async (ini, fim) => {
    try {
      const p = new URLSearchParams();
      if (ini) p.append('inicio', ini);
      if (fim) p.append('fim', fim);
      const r = await axios.get(`${API_URL}/api/dashboard/atendimentos-por-mes?${p.toString()}`, { headers: getAuthHeader() });
      if (r.data && Array.isArray(r.data.por_mes)) {
        setAtendMes(r.data);
        setMesIni(r.data.inicio || ini || '');
        setMesFim(r.data.fim || fim || '');
      }
    } catch (e) { /* mantem o fallback (visaoGeral) */ }
  }, [getAuthHeader]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAtendMes('', ''); }, []);

  const fetchAtividades = useCallback(async () => {
    setLoadingAtividades(true);
    try {
      const [retRes, pagRes, cancRes] = await Promise.all([
        axios.get(`${API_URL}/api/retirada`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/pagamento`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/cancelamentos?limit=2000&slim=1`, { headers: getAuthHeader() }),
      ]);
      setRetiradaData(retRes.data || []);
      setPagamentoData(pagRes.data?.pedidos || []);
      setCancelamentosData(cancRes.data?.cancelamentos || []);
    } catch {
      toast.error('Erro ao carregar atividades');
    } finally {
      setLoadingAtividades(false);
    }
  }, [getAuthHeader]);

  // Canal checks (OK por canal/dia)
  const [canalChecks, setCanalChecks] = useState({});
  const fetchCanalChecks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/canal-checks`, { headers: getAuthHeader() });
      setCanalChecks(res.data.checks || {});
    } catch {}
  }, [getAuthHeader]);
  useEffect(() => { fetchCanalChecks(); }, [fetchCanalChecks]);

  // Perguntas do check (Adnéia, 20/10/2026): canal com perguntas (hoje só a LTM) abre a
  // janela em vez de marcar direto. O backend manda as perguntas com as datas de corte do dia.
  const [checklists, setChecklists] = useState({});
  const [checkCanalAberto, setCheckCanalAberto] = useState('');
  const [respostasCanal, setRespostasCanal] = useState('');
  useEffect(() => {
    axios.get(`${API_URL}/api/dashboard/canal-checklists`, { headers: getAuthHeader() })
      .then(r => setChecklists(r.data.checklists || {}))
      .catch(() => {});
  }, [getAuthHeader]);
  // O "?" de cada atividade (Adnéia, 23/10/2026): o manual de como fazer, pelo nome da atividade.
  const [manuais, setManuais] = useState({});
  const [manualAberto, setManualAberto] = useState('');
  useEffect(() => {
    axios.get(`${API_URL}/api/dashboard/manuais-atividade`, { headers: getAuthHeader() })
      .then(r => setManuais(r.data.manuais || {}))
      .catch(() => {});
  }, [getAuthHeader]);
  const aplicarCheck = (canal, data) => {
    setCanalChecks(prev => {
      const next = { ...prev };
      if (data.checked) next[canal] = data.check || { marcado_por: data.marcado_por || '?' };
      else delete next[canal];
      return next;
    });
  };
  const toggleCanalCheck = async (canal) => {
    if (checklists[canal]) { setCheckCanalAberto(canal); return; }
    try {
      const res = await axios.post(`${API_URL}/api/dashboard/canal-checks`, { canal }, { headers: getAuthHeader() });
      aplicarCheck(canal, res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não gravou o check. Tente de novo.');
    }
  };

  // Formata Date local -> 'YYYY-MM-DD' (sem deslocamento de fuso)
  const fmtLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const fmtBR = (s) => s ? `${s.slice(8, 10)}/${s.slice(5, 7)}` : '';

  // ── Rodízio: quem faz o quê em cada dia (backend GET /api/rodizio/escala; Adnéia, 16/10/2026) ──
  // Embaixo de cada data: quem ficou com a lista azul e a laranja. Ao lado de cada canal,
  // motivo e atividade: quem está com ele hoje (antes do rodízio começar, no próximo dia útil).
  const [escala, setEscala] = useState(null);
  const fetchEscala = useCallback(async () => {
    try {
      const hoje = new Date();
      const ini = new Date(hoje); ini.setDate(ini.getDate() - 45);
      const fim = new Date(hoje); fim.setDate(fim.getDate() + 7);
      const res = await axios.get(`${API_URL}/api/rodizio/escala`,
        { params: { inicio: fmtLocal(ini), fim: fmtLocal(fim) }, headers: getAuthHeader() });
      setEscala(res.data);
    } catch {}
  }, [getAuthHeader]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchEscala(); }, [fetchEscala]);
  const escalaPorDia = {};
  (escala?.dias || []).forEach(d => { escalaPorDia[d.data_br] = d; });
  const escalaRef = escala
    ? ((escala.dias || []).find(d => d.data === escala.hoje) || (escala.dias || []).find(d => d.data > escala.hoje))
    : null;
  // Quem está com o item no dia de referência (hoje, ou o próximo dia útil antes do
  // rodízio começar). Finalizadores (Entregue etc.) não são de ninguém: null.
  const donoDe = (item) => {
    if (!escala || !escalaRef) return null;
    if ((escala.finalizadores || []).includes(item)) return null;
    const lista = escala.lista_do_item?.[item];
    return (lista ? escalaRef.dono_lista?.[lista] : escala.gestao) || null;
  };
  // A cor é da PESSOA, não da lista (Adnéia, 17/10/2026): Caio sempre azul, Gabriela
  // sempre laranja, a gestão roxa. A lista continua invertendo de dono todo dia.
  const donoChip = (item) => {
    const nome = donoDe(item);
    if (!nome) return null;
    const lista = escala.lista_do_item?.[item];
    const quando = escalaRef.data === escala.hoje ? 'hoje' : `em ${escalaRef.data_br}`;
    return (
      <span title={`Com ${nome} ${quando}${lista ? ` (lista ${lista})` : ' (fora das listas: gestão)'}`}
        className={`flex-shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${corPessoa(nome)}`}>
        {nome}
      </span>
    );
  };
  const escalaHeader = (dataBr) => {
    const e = escalaPorDia[dataBr];
    if (!e) return null;
    const dica = `Lista azul: ${e.dono_lista?.azul || '—'} · lista laranja: ${e.dono_lista?.laranja || '—'}`
      + (e.ausentes?.length ? ` · falta: ${e.ausentes.join(', ')}` : '')
      + (e.gravado ? '' : ' (previsto)');
    return (
      <span className="flex flex-col items-center gap-0.5 mt-0.5 leading-tight" title={dica}>
        <span className={`text-[11px] font-semibold px-1 rounded ${corPessoa(e.dono_lista?.azul)}`}>{e.dono_lista?.azul}</span>
        <span className={`text-[11px] font-semibold px-1 rounded ${corPessoa(e.dono_lista?.laranja)}`}>{e.dono_lista?.laranja}</span>
      </span>
    );
  };

  // ── Verificação de status: limpeza por MOTIVO de pendência ──
  // Cronograma: Ag. Parceiro → quarta(3), 1x/semana | demais motivos → terça(2) e quinta(4).
  const [verifRows, setVerifRows] = useState([]);
  const [verifHoje, setVerifHoje] = useState('');
  const fetchVerificacao = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/verificacao-status`, { headers: getAuthHeader() });
      setVerifRows(res.data.rows || []);
      setVerifHoje(res.data.hoje || '');
    } catch {}
  }, [getAuthHeader]);
  useEffect(() => { fetchVerificacao(); }, [fetchVerificacao]);
  // CONFERÊNCIA diária em dias úteis para tudo desde 16/10/2026 (Adnéia, rodízio diário).
  // O ACIONAMENTO vem pronto do backend em `dias_acionar` (_dias_acionar de
  // routes/dashboard.py, rotina de 17/10/2026). O cálculo daqui só vale se o backend
  // ainda não mandar o campo.
  const diasVerif = () => [1, 2, 3, 4, 5];
  const diasAcionar = (motivo) => {
    const row = verifRows.find(r => r.motivo === motivo);
    if (row && Array.isArray(row.dias_acionar)) return row.dias_acionar;
    const m = (motivo || '').trim().toLowerCase();
    if (ATIVIDADES_DIARIAS.has(m)) return [1, 2, 3, 4, 5];
    if (['entregue', 'ag. confirmação de entrega', 'ag. acionar o cliente', 'em devolução - correios'].includes(m)) return [1, 2, 3, 4, 5];
    if (['enviado', 'aguardando', 'ag. compras', 'ag. fornecedor'].includes(m)) return [];
    if (m === 'ag. parceiro') return [3];
    return [2, 4];
  };
  const acionarHoje = (motivo) => diasAcionar(motivo).includes(new Date().getDay());
  const verifDevida = (motivo) => diasVerif(motivo).includes(new Date().getDay());
  // Flags da "Próxima verif.": conferência diária, todo dia útil sem ✓ é devido.
  //   proxima < hoje → atrasada (vermelho) · dia útil sem ✓ → "• hoje".
  // A "próxima" gravada antes de 16/10/2026 podia apontar para a terça ou quinta
  // seguinte; por isso uma data futura não esconde mais o devido de hoje.
  const flagsVerif = (proxima, ultima, motivo, hojeStr) => {
    const feitoHoje = !!ultima && ultima === hojeStr;
    if (feitoHoje) return {};
    if (proxima && proxima < hojeStr) return { devida: true, atraso: true };
    return verifDevida(motivo) ? { devida: true, flagHoje: true } : {};
  };
  const proximaDataVerif = (motivo, baseStr) => {
    const dias = diasVerif(motivo);
    const hojeStr = fmtLocal(new Date());
    const ini = (baseStr && baseStr >= hojeStr) ? new Date(baseStr + 'T12:00:00') : new Date();
    const d = new Date(ini);
    for (let i = 0; i < 14; i++) {
      d.setDate(d.getDate() + 1);
      if (dias.includes(d.getDay())) return fmtLocal(d);
    }
    return '';
  };
  const [verifExpand, setVerifExpand] = useState({});
  // Atualiza linha principal (parceiro vazio) ou sub-linha (parceiro preenchido)
  const updateVerifLocal = (motivo, parceiro, patch) => {
    setVerifRows(prev => prev.map(r => {
      if (r.motivo !== motivo) return r;
      if (!parceiro) return { ...r, ...patch };
      return { ...r, sub: (r.sub || []).map(s => s.parceiro === parceiro ? { ...s, ...patch } : s) };
    }));
  };
  const postVerif = async (motivo, parceiro, fields) => {
    try {
      await axios.post(`${API_URL}/api/dashboard/verificacao-status`, { motivo, parceiro, ...fields }, { headers: getAuthHeader() });
    } catch {}
  };
  const salvarVerifCampo = (motivo, parceiro, fields) => {
    updateVerifLocal(motivo, parceiro, fields);
    postVerif(motivo, parceiro, fields);
  };
  // Check ✓ "feito hoje": carimba ultima=hoje e avança próxima (toggle reversível no backend)
  const toggleVerifCheck = async (motivo, parceiro) => {
    const prox = proximaDataVerif(motivo, fmtLocal(new Date()));
    try {
      const res = await axios.post(`${API_URL}/api/dashboard/verificacao-check`, { motivo, parceiro, proxima: prox }, { headers: getAuthHeader() });
      updateVerifLocal(motivo, parceiro, { ultima: res.data.ultima || '', proxima: res.data.proxima || '',
        check_por: res.data.check_por || '', check_em: res.data.check_em || '' });
      if (res.data.checked) toast.success('Verificado hoje');
    } catch {}
  };

  // Listas de cobrança da Verificação de status (decisão 5, 17/10/2026): transportadora
  // abre a janela com o rastreio; a da Camila é o Relatório Ag. Logística de sempre.
  const [cobranca, setCobranca] = useState(null);   // 'J&T' | 'Total' | 'Asap' | null
  // Histórico de um status ou atividade ({ motivo, parceiro } | null) e os portais da aba
  // Acessos ({ canal: link }), para o "Abrir portal" ao lado de cada status.
  const [histAlvo, setHistAlvo] = useState(null);
  const [portais, setPortais] = useState({});
  // Só depois de ler a aba Acessos dá para saber quem não tem portal (e ganha o e-mail).
  const [portaisLidos, setPortaisLidos] = useState(false);
  useEffect(() => {
    if (!veAcessos) return;
    axios.get(`${API_URL}/api/dashboard/acessos`, { headers: getAuthHeader() })
      .then(r => {
        setPortais(Object.fromEntries((r.data.rows || []).filter(x => x.link).map(x => [x.canal, x.link])));
        setPortaisLidos(true);
      })
      .catch(() => {});
  }, [veAcessos, getAuthHeader]);
  const linkPortal = (nome, rotulo) => {
    const href = linkDoPortal(nome, portais);
    if (!href) return null;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer"
        className="mt-1 self-start inline-flex items-center gap-1 text-xs font-semibold text-blue-700 dark:text-blue-300 hover:underline">
        <ExternalLink className="h-3 w-3" /> Abrir {rotulo || nome}
      </a>
    );
  };
  const botaoHist = (motivo, parceiro = '') => (
    <button type="button" onClick={() => setHistAlvo({ motivo, parceiro })} title="Ver o histórico do ✓"
      className="ml-1 inline-flex align-middle text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
      <History className="h-3.5 w-3.5" />
    </button>
  );
  const relatorioCamila = async () => {
    try {
      toast.info('Gerando o relatório da Camila (Ag. Logística)…');
      const r = await axios.get(`${API_URL}/api/relatorios/ag-logistica-xlsx`, { headers: getAuthHeader(), responseType: 'blob' });
      baixarBlob(r.data, `relatorio_ag_logistica_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.xlsx`);
    } catch { toast.error('Não gerou o relatório Ag. Logística'); }
  };

  // ── Verificação de ATIVIDADES (Disp. Retirada, Pag. Não Aprovado, AES, ETR, Erro na Nota) ──
  // Mesma mecânica da Verificação de status, tabela separada. Todas diárias (dias úteis).
  const [ativRows, setAtivRows] = useState([]);
  const [ativHoje, setAtivHoje] = useState('');
  const fetchVerificacaoAtividades = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/verificacao-atividades`, { headers: getAuthHeader() });
      setAtivRows(res.data.rows || []);
      setAtivHoje(res.data.hoje || '');
    } catch {}
  }, [getAuthHeader]);
  useEffect(() => { fetchVerificacaoAtividades(); }, [fetchVerificacaoAtividades]);
  const updateAtivLocal = (motivo, patch) => setAtivRows(prev => prev.map(r => r.motivo === motivo ? { ...r, ...patch } : r));

  // ── Importar dados (Adnéia, 29/10/2026) ──────────────────────────────
  // As três bases do dia, cada uma numa sub-linha com ✓, observação e o botão que sobe o
  // arquivo ali mesmo (a mesma rota da Base ELO). O ✓ é de quem fez: subir não marca.
  const BASES_DO_DIA = { 'Tracking J&T': 'base-jt', 'Tracking Total': 'base-total', 'Base AET': 'base-aet' };
  const [importarAberto, setImportarAberto] = useState(false);
  const [subindoBase, setSubindoBase] = useState('');
  const updateAtivSub = (motivo, parceiro, patch) => setAtivRows(prev => prev.map(r => (r.motivo !== motivo ? r
    : { ...r, sub: (r.sub || []).map(s => (s.parceiro === parceiro ? { ...s, ...patch } : s)) })));
  const toggleAtivSubCheck = async (motivo, parceiro) => {
    try {
      const res = await axios.post(`${API_URL}/api/dashboard/verificacao-check`,
        { motivo, parceiro, proxima: proximaDataVerif(motivo, fmtLocal(new Date())) }, { headers: getAuthHeader() });
      updateAtivSub(motivo, parceiro, { ultima: res.data.ultima || '', check_por: res.data.check_por || '',
        check_em: res.data.check_em || '' });
      fetchVerificacaoAtividades();   // o "em aberto" da linha conta as bases sem ✓
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não gravou o ✓. Tente de novo.');
    }
  };
  const subirBase = async (nome, ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = '';
    if (!f || !BASES_DO_DIA[nome]) return;
    setSubindoBase(nome);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await axios.post(`${API_URL}/api/${BASES_DO_DIA[nome]}/importar`, fd,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      if (data && data.ok === false) toast.error(`${nome}: ${data.message || 'não importou'}`);
      else toast.success(`${nome}: ${(data && data.message) || 'importado'}`, { duration: 12000 });
    } catch (e) {
      toast.error(`${nome}: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSubindoBase('');
    }
  };

  // ── Atividades de SALDO LANÇADO ──────────────────────────────────────
  // Substituem planilhas mantidas à mão. A pessoa lança entrou/saiu do dia;
  // o em aberto é calculado no backend (saldo inicial + entrou − saiu) e por
  // isso NÃO é editável aqui — dois números digitados que deveriam bater
  // acabam não batendo, que era o defeito da planilha.
  //
  // As duas têm um item só e mostram os campos na própria linha (`item_unico`
  // vem do backend) — abrir pra ver uma linha só seria clique à toa. A de
  // transportadora saiu daqui em 29/10/2026: o número vem da tela de pendências.
  const ATIVS_SALDO = ['Ocorrência GRS', 'Atendimento'];
  const [saldoAtivs, setSaldoAtivs] = useState({});   // atividade -> resposta do GET
  const [saldoAberto, setSaldoAberto] = useState({}); // atividade -> expandida?
  const [saldoSalvando, setSaldoSalvando] = useState('');
  const fetchSaldos = useCallback(async () => {
    const pares = await Promise.all(ATIVS_SALDO.map(async atv => {
      try {
        const res = await axios.get(`${API_URL}/api/pendencia-transportadora`,
          { params: { atividade: atv }, headers: getAuthHeader() });
        return [atv, res.data];
      } catch { return [atv, null]; }
    }));
    const ok = pares.filter(([, v]) => v);
    // MESCLA, não substitui: um refetch que falhe (o do catch do salvar, por
    // exemplo) apagaria o que já estava carregado, e a atividade sumiria da
    // tela — sem o "+", sem os campos, indistinguível de não ser atividade de
    // saldo. A pessoa perderia a capacidade de lançar sem nenhuma mensagem.
    if (ok.length) setSaldoAtivs(p => ({ ...p, ...Object.fromEntries(ok) }));
    const falharam = pares.filter(([, v]) => !v).map(([a]) => a);
    if (falharam.length) toast.error('Não carregou o saldo de: ' + falharam.join(', '));
  }, [getAuthHeader]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSaldos(); }, [fetchSaldos]);
  // Manda SÓ o campo que mudou. A primeira versão mandava o par inteiro,
  // lido do que a tela tinha em memória: painel aberto desde as 8h, alguém
  // lança entrou=12 às 10h, o operador digita saiu=4 às 14h na aba velha e o
  // POST levava entrou=0 junto, apagando o 12 sem erro nenhum.
  const salvarSaldo = async (atividade, transportadora, campo, valor) => {
    const dados = saldoAtivs[atividade] || {};
    const linha = (dados.linhas || []).find(l => l.transportadora === transportadora) || {};
    const n = Math.max(0, parseInt(valor, 10) || 0);
    if (n === (linha[campo] ?? 0)) return;          // nada mudou, não grava
    setSaldoSalvando(atividade + transportadora + campo);
    try {
      const res = await axios.post(`${API_URL}/api/pendencia-transportadora`, {
        atividade, transportadora, campo, valor: n,
      }, { headers: getAuthHeader() });
      setSaldoAtivs(p => ({ ...p, [atividade]: res.data }));
      // o saldo total muda, então a linha da tabela precisa acompanhar
      updateAtivLocal(atividade, { em_aberto: res.data.total_aberto });
    } catch (e) {
      toast.error('Não salvou: ' + (e?.response?.data?.detail || e?.message || ''));
      fetchSaldos();
    } finally { setSaldoSalvando(''); }
  };
  // Em aberto de cada atividade: Tem em Casa e as de saldo vêm contadas do backend
  // (em_aberto); as outras se contam com o que o Dashboard já carregou.
  const qtdAtivDe = (r) => qtdAtividade(r, { retirada: retiradaData, pagamento: pagamentoData, cancelamentos: cancelamentosData });
  const toggleAtivCheck = async (motivo) => {
    const prox = proximaDataVerif(motivo, fmtLocal(new Date()));
    try {
      const res = await axios.post(`${API_URL}/api/dashboard/verificacao-check`, { motivo, parceiro: '', proxima: prox }, { headers: getAuthHeader() });
      updateAtivLocal(motivo, { ultima: res.data.ultima || '', proxima: res.data.proxima || '',
        check_por: res.data.check_por || '', check_em: res.data.check_em || '' });
      if (res.data.checked) toast.success('Feito hoje');
    } catch (e) {
      // Ex.: atividade com perguntas e a lista delas ainda não carregou (F5 resolve).
      toast.error(e?.response?.data?.detail || 'Não gravou o ✓. Tente de novo.');
    }
  };

  const fetchFiltros = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/v2/filtros`, { headers: getAuthHeader() });
      setFiltros(res.data);
    } catch (e) { console.error(e); }
  }, [getAuthHeader]);

  const fetchTabData = useCallback(async (tab) => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append('periodo_dias', periodoDias);
    if (canalFiltro) params.append('canal', canalFiltro);
    if (fornecedorFiltro) params.append('fornecedor', fornecedorFiltro);

    try {
      const endpoint = `${API_URL}/api/dashboard/v2/${tab}?${params.toString()}`;
      const res = await axios.get(endpoint, { headers: getAuthHeader() });

      switch (tab) {
        case 'visao-geral': setVisaoGeral(res.data); break;
        case 'volume-canal': setVolumeCanal(res.data); break;
        case 'classificacao': setClassificacao(res.data); break;
        case 'performance':
          setPerformance(res.data);
          try {
            const rv = await axios.get(`${API_URL}/api/dashboard/v2/visao-geral?${params.toString()}`, { headers: getAuthHeader() });
            setVisaoGeral(rv.data);
          } catch (e) {}
          break;
        case 'pendencias': setPendencias(res.data); break;
        case 'estornos': setEstornos(res.data); break;
        case 'reincidencia': setReincidencia(res.data); break;
      }
      setLastUpdated(new Date());
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [periodoDias, canalFiltro, fornecedorFiltro, getAuthHeader]);

  // Modal combinado na reunião (card Falha Compras por fornecedor): só registro, só a gestão.
  // Erro sobe para o card mostrar na linha.
  const salvarModalCombinado = async (marca, dias) => {
    await axios.put(`${API_URL}/api/dashboard/modal-combinado`, { marca, dias }, { headers: getAuthHeader() });
    toast.success(dias === '' ? `Modal combinado de ${marca} tirado.` : `Modal combinado de ${marca}: ${dias} dias úteis.`);
    fetchTabData('classificacao');
  };

  useEffect(() => { fetchFiltros(); }, [fetchFiltros]);
  useEffect(() => {
    if (activeTab === 'acessos') return;   // a aba Acessos busca os próprios dados
    fetchTabData(activeTab);
  }, [activeTab, periodoDias, canalFiltro, fornecedorFiltro, fetchTabData]);

  useEffect(() => {
    if (activeTab === 'visao-geral') fetchAtividades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'slate', onClick }) => (
    <Card className={`${onClick ? 'cursor-pointer hover:bg-accent/50 transition-colors' : ''}`} onClick={onClick}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        {Icon && <Icon className={`h-4 w-4 text-${color}-500`} />}
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value}</div>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );

  // ABA 1 - VISÃO GERAL
  const getTaxaColor = (taxa) => {
    if (taxa <= 0) return 'text-muted-foreground';
    if (taxa < 5) return 'text-emerald-600';
    if (taxa < 10) return 'text-amber-600';
    return 'text-red-600';
  };

  const TabVisaoGeral = () => (
    <div className="space-y-6">
      {/* As Missões do dia moram na janela do botão Rodízio / Minhas demandas desde 17/10/2026 (Adnéia). */}
      {/* Tabela de Atendimentos por Canal e Dia */}
      {(() => {
        // Só hoje e o dia útil anterior abertos; o resto do passado fica no "+" de cada
        // mês (Adnéia, 17/10/2026, decisão 1).
        const RECENT_DAYS = 2;
        // Próximo dia útil (coluna azul do fim): pula sábado e domingo
        const proxDiaBr = (() => {
          const proximoDia = new Date();
          proximoDia.setDate(proximoDia.getDate() + 1);
          if (proximoDia.getDay() === 6) proximoDia.setDate(proximoDia.getDate() + 2);
          else if (proximoDia.getDay() === 0) proximoDia.setDate(proximoDia.getDate() + 1);
          return proximoDia.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
        })();
        const monthNames = {'01':'Jan','02':'Fev','03':'Mar','04':'Abr','05':'Mai','06':'Jun','07':'Jul','08':'Ago','09':'Set','10':'Out','11':'Nov','12':'Dez'};
        const allDias = visaoGeral?.dias_headers || [];
        const recentDias = allDias.slice(-RECENT_DAYS);
        const olderDias = allDias.slice(0, -RECENT_DAYS);
        const olderMonthsOrder = [];
        const olderByMonth = {};
        olderDias.forEach(dia => {
          const m = dia.data.split('/')[1];
          if (!olderByMonth[m]) { olderByMonth[m] = []; olderMonthsOrder.push(m); }
          olderByMonth[m].push(dia);
        });

        // getValue devolve o número cru: AR/A/F vazios viram 0; o E vazio vira "?" (dia antes
        // de o ELO guardar o dia de cada edição, 20/10/2026).
        const renderDayCells = (dia, idx, getValue) => {
          const n = (k) => getValue(dia, k) ?? 0;
          const e = getValue(dia, 'e');
          return (
          <React.Fragment key={`cell-${dia.data}`}>
            <TableCell className={`text-center px-1 ${idx % 2 === 0 ? 'bg-yellow-50/30' : 'bg-yellow-50/50'}`}>
              <span className={`text-sm ${n('ar') > 0 ? 'font-semibold text-yellow-700' : 'text-muted-foreground'}`}>{n('ar')}</span>
            </TableCell>
            <TableCell className={`text-center px-1 ${idx % 2 === 0 ? 'bg-orange-50/30' : 'bg-orange-50/50'}`}>
              <span className={`text-sm ${n('a') > 0 ? 'font-semibold text-orange-700' : 'text-muted-foreground'}`}>{n('a')}</span>
            </TableCell>
            <TableCell className={`text-center px-1 ${idx % 2 === 0 ? 'bg-sky-50/30' : 'bg-sky-50/50'}`}>
              {e == null
                ? <span className="text-sm text-muted-foreground" title="Antes de 20/10/2026 o ELO não guardava o dia de cada edição">?</span>
                : <span className={`text-sm ${e > 0 ? 'font-semibold text-sky-700 dark:text-sky-400' : 'text-muted-foreground'}`}>{e}</span>}
            </TableCell>
            <TableCell className={`text-center px-1 border-r ${idx % 2 === 0 ? 'bg-emerald-50/30' : 'bg-emerald-50/50'}`}>
              <span className={`text-sm ${n('f') > 0 ? 'font-semibold text-emerald-700' : 'text-muted-foreground'}`}>{n('f')}</span>
            </TableCell>
          </React.Fragment>
          );
        };

        return (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5" />
                Atendimentos por Canal
              </CardTitle>
              <CardDescription>
                AR = Aguardando Resposta | A = Aberto | E = Editado | F = Fechado · Clique no mês para expandir dias anteriores
                <br /><strong>E (Editado)</strong>: atendimento que já estava aberto, alguém salvou naquele dia e continua aberto (o que abriu no dia está no AR; o que fechou, no F). <strong>?</strong> = antes de 20/10/2026 o ELO não guardava o dia de cada edição.
                <br />O selo ao lado do canal é o <strong>SLA de primeira resposta</strong>: prazo em dias úteis para marcar o check verde (não é prazo para encerrar o atendimento).
                <br /><strong>Rodízio:</strong> embaixo de cada data, quem ficou com cada lista (no próximo dia útil, o previsto). Ao lado do canal, quem está com ele hoje. A cor é da pessoa: <span className="text-blue-700 dark:text-blue-300 font-semibold">Caio</span> sempre azul, <span className="text-orange-700 dark:text-orange-300 font-semibold">Gabriela</span> sempre laranja.
                <br /><strong>ZAP/E-mail</strong> mede a <strong>captação direta</strong> — quem falou por WhatsApp ou e-mail sem passar pelo canal de venda. O atendimento continua contado na linha do parceiro, então essa linha <strong>não soma</strong> no total do dia.
              </CardDescription>
              {/* SLA de atendimento (Adnéia, 22/09): verde só quando TODOS os canais com
                  prazo definido estão 100% dentro. Senão, um selo por canal em falta. */}
              {visaoGeral?.sla_resumo && (
                visaoGeral.sla_resumo.ok ? (
                  <div className="mt-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900 rounded-md px-3 py-1.5">
                    ✅ SLA de atendimento dentro do prazo
                  </div>
                ) : (
                  <div className="mt-2 rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 px-3 py-2">
                    <div className="text-sm font-bold text-red-700 dark:text-red-300">
                      ⚠️ SLA de atendimento não atendido em {visaoGeral.sla_resumo.violados.length} de {visaoGeral.por_canal_dia?.filter(c => c.sla_dias != null).length || 0} canais
                      <span className="font-normal"> · últimos {visaoGeral.sla_resumo.periodo_dias} dias</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {visaoGeral.sla_resumo.violados.map(v => (
                        <span
                          key={v.canal}
                          title={`Prazo ${v.sla_dias} dia(s) útil(eis) para o check verde · ${v.dias_sem_check} dia(s) sem check · último em ${v.ultimo_check || '—'}`}
                          className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 whitespace-nowrap"
                        >
                          SLA {v.canal} não atendido
                        </span>
                      ))}
                    </div>
                  </div>
                )
              )}
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table style={{width: 'max-content', tableLayout: 'auto'}}>
                  <TableHeader>
                    {/* Linha 1: meses colapsados + dias recentes */}
                    <TableRow>
                      <TableHead rowSpan={2} className="font-semibold min-w-[150px] align-bottom border-r">Canal</TableHead>
                      {/* Meses antigos */}
                      {olderMonthsOrder.map(month =>
                        expandedMonths[month]
                          ? olderByMonth[month].map((dia, idx) => (
                              <TableHead key={dia.data} colSpan={4} className={`text-center border-r ${idx % 2 === 0 ? 'bg-slate-50 dark:bg-slate-900/30' : 'bg-slate-100 dark:bg-slate-800/30'}`}>
                                <div className="flex flex-col items-center py-1">
                                  <span className="font-semibold text-sm">{dia.data}</span>
                                  {escalaHeader(dia.data)}
                                  {idx === 0 && (
                                    <button onClick={() => toggleMonth(month)} className="text-[11px] text-blue-500 hover:text-blue-700 mt-0.5 leading-none">
                                      {monthNames[month]} −
                                    </button>
                                  )}
                                </div>
                              </TableHead>
                            ))
                          : (
                            <TableHead key={month} rowSpan={2} className="text-center border-r bg-slate-100 dark:bg-slate-800/40 align-middle p-0" style={{width:'28px', minWidth:'28px', maxWidth:'28px'}}>
                              <button onClick={() => toggleMonth(month)}
                                className="flex flex-col items-center gap-0 w-full py-1 px-0.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors">
                                <span className="text-[9px] font-semibold text-slate-500 dark:text-slate-400 leading-tight">{monthNames[month]}</span>
                                <span className="text-blue-500 font-bold text-[11px] leading-tight">+</span>
                              </button>
                            </TableHead>
                          )
                      )}
                      {/* Últimos 5 dias */}
                      {recentDias.map((dia, idx) => (
                        <TableHead key={dia.data} colSpan={4} className={`text-center border-r ${idx % 2 === 0 ? 'bg-slate-50 dark:bg-slate-900/30' : 'bg-slate-100 dark:bg-slate-800/30'}`}>
                          <div className="flex flex-col items-center py-1">
                            <span className="font-semibold text-sm">{dia.data}</span>
                            {escalaHeader(dia.data)}
                          </div>
                        </TableHead>
                      ))}
                      {/* Próximo dia útil */}
                      <TableHead colSpan={1} className="text-center border-r bg-indigo-50 dark:bg-indigo-900/30">
                        <div className="flex flex-col items-center py-1">
                          <span className="font-semibold text-sm">{proxDiaBr}</span>
                          {escalaHeader(proxDiaBr)}
                        </div>
                      </TableHead>
                    </TableRow>
                    {/* Linha 2: AR/A/F */}
                    <TableRow>
                      {olderMonthsOrder.map(month =>
                        expandedMonths[month]
                          ? olderByMonth[month].map((dia, idx) => (
                              <React.Fragment key={`h2-${dia.data}`}>
                                <TableHead className={`text-center w-10 text-sm px-1 ${idx % 2 === 0 ? 'bg-yellow-50' : 'bg-yellow-100'}`}><span className="text-yellow-700 font-semibold">AR</span></TableHead>
                                <TableHead className={`text-center w-10 text-sm px-1 ${idx % 2 === 0 ? 'bg-orange-50' : 'bg-orange-100'}`}><span className="text-orange-700 font-semibold">A</span></TableHead>
                                <TableHead className={`text-center w-10 text-sm px-1 ${idx % 2 === 0 ? 'bg-sky-50' : 'bg-sky-100'}`} title="Editados: já estavam abertos, alguém salvou no dia e continuam abertos"><span className="text-sky-700 font-semibold">E</span></TableHead>
                                <TableHead className={`text-center w-10 text-sm px-1 border-r ${idx % 2 === 0 ? 'bg-emerald-50' : 'bg-emerald-100'}`}><span className="text-emerald-700 font-semibold">F</span></TableHead>
                              </React.Fragment>
                            ))
                          : null
                      )}
                      {recentDias.map((dia, idx) => (
                        <React.Fragment key={`h2r-${dia.data}`}>
                          <TableHead className={`text-center w-10 text-sm px-1 ${idx % 2 === 0 ? 'bg-yellow-50 dark:bg-yellow-950/30' : 'bg-yellow-100 dark:bg-yellow-900/30'}`}><span className="text-yellow-700 dark:text-yellow-400 font-semibold">AR</span></TableHead>
                          <TableHead className={`text-center w-10 text-sm px-1 ${idx % 2 === 0 ? 'bg-orange-50 dark:bg-orange-950/30' : 'bg-orange-100 dark:bg-orange-900/30'}`}><span className="text-orange-700 dark:text-orange-400 font-semibold">A</span></TableHead>
                          <TableHead className={`text-center w-10 text-sm px-1 ${idx % 2 === 0 ? 'bg-sky-50 dark:bg-sky-950/30' : 'bg-sky-100 dark:bg-sky-900/30'}`} title="Editados: já estavam abertos, alguém salvou no dia e continuam abertos"><span className="text-sky-700 dark:text-sky-400 font-semibold">E</span></TableHead>
                          <TableHead className={`text-center w-10 text-sm px-1 border-r ${idx % 2 === 0 ? 'bg-emerald-50 dark:bg-emerald-950/30' : 'bg-emerald-100 dark:bg-emerald-900/30'}`}><span className="text-emerald-700 dark:text-emerald-400 font-semibold">F</span></TableHead>
                        </React.Fragment>
                      ))}
                      <TableHead className="text-center w-10 text-sm px-1 bg-indigo-100 border-r"><span className="text-indigo-700 font-semibold">A</span></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Linha total */}
                    <TableRow className="bg-blue-50/50 dark:bg-blue-950/30 font-semibold">
                      <TableCell className="font-semibold border-r">Atendimento ao cliente</TableCell>
                      {olderMonthsOrder.map(month =>
                        expandedMonths[month]
                          ? olderByMonth[month].map((dia, idx) => renderDayCells(dia, idx, (d, k) => visaoGeral?.totais_por_dia?.[d.data]?.[k]))
                          : <TableCell key={`tot-col-${month}`} className="border-r bg-slate-50/50 dark:bg-slate-800/20 p-0" style={{width:'28px', minWidth:'28px', maxWidth:'28px'}} />
                      )}
                      {recentDias.map((dia, idx) => renderDayCells(dia, idx, (d, k) => visaoGeral?.totais_por_dia?.[d.data]?.[k]))}
                      <TableCell className="text-center px-1 bg-indigo-100/50 border-r">
                        <span className="font-bold text-indigo-700">{visaoGeral?.pendentes || 0}</span>
                      </TableCell>
                    </TableRow>
                    {/* Canais individuais */}
                    {visaoGeral?.por_canal_dia?.map((item) => {
                      const isOk = !!canalChecks[item.canal];
                      // Amarelo = "feito com pendência" (perguntas do check): não conta no SLA até virar verde.
                      const isPend = isOk && canalChecks[item.canal]?.situacao === 'pendencia';
                      const lista = checklists[item.canal];
                      return (
                      <TableRow
                        key={item.canal}
                        className={`${item.informativa ? 'border-t-2 border-t-blue-300 dark:border-t-blue-800 bg-blue-50/40 dark:bg-blue-950/20 ' : ''}${isPend ? 'bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100/70' : (isOk ? 'bg-emerald-50 dark:bg-emerald-950/30 hover:bg-emerald-100/70' : 'hover:bg-muted/50')}`}
                      >
                        <TableCell className="font-medium border-r text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              title={lista
                                ? (isOk ? `${resumoDoCheck(canalChecks[item.canal], lista)}\n\nClique para responder de novo ou desmarcar` : 'Responder as perguntas e marcar')
                                : (isOk ? `OK marcado por ${canalChecks[item.canal]?.marcado_por} — clique para desmarcar` : 'Marcar como OK')}
                              onClick={() => toggleCanalCheck(item.canal)}
                              className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center text-sm font-bold transition-all ${
                                isPend
                                  ? 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600'
                                  : isOk
                                  ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600'
                                  : 'border-slate-300 text-slate-300 hover:border-emerald-400 hover:text-emerald-400'
                              }`}
                            >{isPend ? '!' : '✓'}</button>
                            <span className={isPend ? 'text-amber-700 dark:text-amber-400 font-semibold' : (isOk ? 'text-emerald-700 dark:text-emerald-400 font-semibold' : '')}>{item.canal}</span>
                            {/* Portal do canal, da aba Acessos (Adnéia, 17/10/2026) */}
                            {linkDoPortal(item.canal, portais) ? (
                              <a href={linkDoPortal(item.canal, portais)} target="_blank" rel="noopener noreferrer"
                                title={`Abrir o portal ${item.canal}`} onClick={(e) => e.stopPropagation()}
                                className="flex-shrink-0 text-blue-600 hover:text-blue-800 dark:text-blue-300">
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            ) : (portaisLidos && (
                              /* Sem portal na aba Acessos: o canal é por e-mail (Adnéia, 23/10/2026) */
                              <a href={EMAIL_ATENDIMENTO} target="_blank" rel="noopener noreferrer"
                                title={`${item.canal} não tem portal: abrir o e-mail do atendimento`} onClick={(e) => e.stopPropagation()}
                                className="flex-shrink-0 text-blue-600 hover:text-blue-800 dark:text-blue-300">
                                <Mail className="h-3.5 w-3.5" />
                              </a>
                            ))}
                            {donoChip(item.canal)}
                            {/* Lista LL do portal (Adnéia, 29/10/2026): vermelho até importar, verde depois */}
                            {item.canal === 'LL Loyalty' && <BotaoListaLL getAuthHeader={getAuthHeader} user={user} />}
                            {/* Lista CSU, como a da LL (Adnéia, 03/11/2026) */}
                            {item.canal === 'CSU' && <BotaoListaCSU getAuthHeader={getAuthHeader} user={user} />}
                            {item.informativa && (
                              <span
                                title="Captação direta: cliente que falou por WhatsApp/e-mail em vez de entrar pelo canal de venda. O atendimento continua contado na linha do parceiro dele, por isso esta linha NÃO soma no total do dia."
                                className="flex-shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 whitespace-nowrap"
                              >
                                captação · não soma
                              </span>
                            )}
                            {item.sla_dias != null && (
                              <span
                                title={`SLA de PRIMEIRA RESPOSTA: ${item.sla_dias} dia(s) útil(eis) para marcar o check verde (não é prazo para encerrar).`
                                  + ` Último check: ${item.sla_ultimo_check || '—'}`
                                  + (item.sla_dias_sem_check >= 0 ? ` · ${item.sla_dias_sem_check} dia(s) útil(eis) atrás` : '')
                                  + (item.sla_pct != null ? ` · cumprimento no período: ${item.sla_pct}%` : '')}
                                className={`flex-shrink-0 text-[11px] font-bold px-1.5 py-0.5 rounded ${!item.sla_ok
                                  ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                                  : (item.sla_dias_sem_check >= item.sla_dias - 1
                                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                                      : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300')}`}
                              >
                                {item.sla_dias}d{item.sla_pct != null ? ` · ${item.sla_pct}%` : ''}
                              </span>
                            )}
                            {/* Respostas dos últimos 30 dias: só a gestão (decisão 6, 20/10/2026) */}
                            {lista && user?.role === 'admin' && (
                              <button type="button" onClick={() => setRespostasCanal(item.canal)}
                                title={`Respostas do check da ${item.canal}, últimos 30 dias`}
                                className="flex-shrink-0 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-muted dark:text-slate-300">
                                <History className="h-3 w-3" /> Respostas
                              </button>
                            )}
                          </div>
                        </TableCell>
                        {olderMonthsOrder.map(month =>
                          expandedMonths[month]
                            ? olderByMonth[month].map((dia, idx) => renderDayCells(dia, idx, (d, k) => item.dias[d.data]?.[k]))
                            : <TableCell key={`${item.canal}-col-${month}`} className="border-r bg-slate-50/30 dark:bg-slate-800/10 p-0" style={{width:'28px', minWidth:'28px', maxWidth:'28px'}} />
                        )}
                        {recentDias.map((dia, idx) => renderDayCells(dia, idx, (d, k) => item.dias[d.data]?.[k]))}
                        <TableCell className="text-center px-1 bg-indigo-100/30 border-r">
                          <span className={`text-sm ${item.total?.a > 0 ? 'font-semibold text-indigo-700' : 'text-muted-foreground'}`}>{item.total?.a || 0}</span>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    {/* ── Demais atividades ── */}
                    {false /* atividades movidas para a tabela "Verificação de atividades" (evita duplicação) */ && (() => {
                      const cancelamentos = cancelamentosData || [];
                      const hojeStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD do dia local
                      const filtraCanc = (tipo) => cancelamentos.filter(c => c.tipo === tipo);
                      // "pendente" = status !== encerrado; "tratado" = encerrado HOJE
                      const cancStats = (tipo) => {
                        const arr = filtraCanc(tipo);
                        return {
                          pendente: arr.filter(c => c.status !== 'encerrado').length,
                          tratado:  arr.filter(c => c.status === 'encerrado' && (c.data_encerramento === hojeStr || (c.updated_at || '').startsWith(hojeStr))).length,
                        };
                      };
                      // Stats diárias por tipo: { aes: { 'DD/MM': {ar: 1, f: 8}, ... }, etr: {...}, erro_nota: {...} }
                      // AR = entradas (data_criacao naquele dia) | F = saídas (data_encerramento naquele dia)
                      // Chave em DD/MM porque é o formato usado em dia.data vindo do backend
                      const toDDMM = (yyyymmdd) => {
                        if (!yyyymmdd || yyyymmdd.length < 10) return '';
                        return `${yyyymmdd.substring(8, 10)}/${yyyymmdd.substring(5, 7)}`;
                      };
                      const cancDailyStats = (() => {
                        const m = { aes: {}, etr: {}, erro_nota: {} };
                        for (const c of cancelamentos) {
                          const tipo = c.tipo;
                          if (!m[tipo]) continue;
                          const dCri = toDDMM(c.data_criacao || '');
                          if (dCri) {
                            if (!m[tipo][dCri]) m[tipo][dCri] = { ar: 0, f: 0 };
                            m[tipo][dCri].ar++;
                          }
                          if (c.status === 'encerrado' && c.data_encerramento) {
                            const dEnc = toDDMM(c.data_encerramento);
                            if (dEnc) {
                              if (!m[tipo][dEnc]) m[tipo][dEnc] = { ar: 0, f: 0 };
                              m[tipo][dEnc].f++;
                            }
                          }
                        }
                        return m;
                      })();
                      const cancDia = (tipo, data) => cancDailyStats[tipo]?.[data] || { ar: 0, f: 0 };

                      // Saldo rolante por tipo (mesma regra dos canais):
                      //   AR = criados no dia | F = encerrados no dia | A = saldo de abertura
                      //   (criados ANTES do dia e ainda não encerrados). Result = A + AR - F.
                      const cancListas = { aes: [], etr: [], erro_nota: [] };
                      for (const c of cancelamentos) {
                        if (!cancListas[c.tipo]) continue;
                        const cri = (c.data || c.data_criacao || '').substring(0, 10);
                        const enc = (c.status === 'encerrado' && c.data_encerramento)
                          ? String(c.data_encerramento).substring(0, 10) : '';
                        cancListas[c.tipo].push({ cri, enc });
                      }
                      const ddmmToYMD = (ddmm) => {
                        if (!ddmm || ddmm.length < 5) return '';
                        const [dd, mm] = ddmm.split('/');
                        return `2026-${mm}-${dd}`;
                      };
                      const cancRolling = (tipo, ddmm) => {
                        const D = ddmmToYMD(ddmm);
                        const list = cancListas[tipo] || [];
                        let ar = 0, f = 0, a = 0;
                        for (const x of list) {
                          if (x.cri === D) ar++;
                          if (x.enc === D) f++;
                          if (x.cri && x.cri < D && (x.enc === '' || x.enc >= D)) a++;
                        }
                        return { ar, a, f };
                      };

                      // Retirada/Pagamento também rolam: entrada = criado_em;
                      // saída = data de resolução (finalizado_em / updated_at) quando há status_final.
                      const buildRol = (arr, campoFim) => (arr || []).map(p => ({
                        cri: String(p.criado_em || p.registrado_em || '').substring(0, 10),
                        enc: p.status_final ? String(p[campoFim] || '').substring(0, 10) : '',
                      }));
                      const rolListas = {
                        'atividade:retirada': buildRol(retiradaData, 'finalizado_em'),
                        'atividade:pagamento': buildRol(pagamentoData, 'updated_at'),
                      };
                      const listaRolling = (lista, ddmm) => {
                        const D = ddmmToYMD(ddmm);
                        let ar = 0, f = 0, a = 0;
                        for (const x of (lista || [])) {
                          if (x.cri === D) ar++;
                          if (x.enc === D) f++;
                          if (x.cri && x.cri < D && (x.enc === '' || x.enc >= D)) a++;
                        }
                        return { ar, a, f };
                      };
                      // Saldo rolante para QUALQUER linha de atividade (cancelamento ou retirada/pagamento)
                      const rowRolling = (atv, ddmm) =>
                        atv.cancTipo ? cancRolling(atv.cancTipo, ddmm)
                                     : listaRolling(rolListas[atv.key], ddmm);
                      const sAes = cancStats('aes');
                      const sEtr = cancStats('etr');
                      const sErr = cancStats('erro_nota');

                      // "Pendente" (coluna resumo) = backlog atual = soma de itens não-encerrados.
                      const demPendente =
                        (retiradaData || []).filter(p => !p.status_final).length
                        + (pagamentoData || []).filter(p => !p.status_final).length
                        + sAes.pendente + sEtr.pendente + sErr.pendente;
                      const lastDia = recentDias[recentDias.length - 1];
                      const zero = <span className="text-muted-foreground text-sm">0</span>;

                      const atividadesRows = [
                        {
                          key: 'atividade:retirada',
                          label: 'Disp. p/ Retirada',
                          pendente: (retiradaData || []).filter(p => !p.status_final).length,
                          tratado:  (retiradaData || []).filter(p => !!p.status_final).length,
                          path: '/retirada',
                        },
                        {
                          key: 'atividade:pagamento',
                          label: 'Pag. Não Aprovado',
                          pendente: (pagamentoData || []).filter(p => !p.status_final).length,
                          tratado:  (pagamentoData || []).filter(p => !!p.status_final).length,
                          path: '/pagamento',
                        },
                        {
                          key: 'atividade:canc_aes',
                          label: 'Cancelamentos (AES)',
                          pendente: sAes.pendente,
                          tratado:  sAes.tratado,
                          path: '/cancelamentos',
                          cancTipo: 'aes',
                        },
                        {
                          key: 'atividade:canc_etr',
                          label: 'Falta na Produção (ETR)',
                          pendente: sEtr.pendente,
                          tratado:  sEtr.tratado,
                          path: '/etr',
                          cancTipo: 'etr',
                        },
                        {
                          key: 'atividade:canc_erro_nota',
                          label: 'Erro na Nota',
                          pendente: sErr.pendente,
                          tratado:  sErr.tratado,
                          path: '/erro-nota',
                          cancTipo: 'erro_nota',
                        },
                      ];


                      return (
                        <>
                          {/* Linha cabeçalho "Demais atividades" */}
                          <TableRow className="bg-rose-50/60 dark:bg-rose-950/20 font-semibold border-t-2 border-rose-200 dark:border-rose-800">
                            <TableCell className="font-semibold border-r text-rose-700 dark:text-rose-400 text-sm pl-3">
                              Demais atividades
                            </TableCell>
                            {olderMonthsOrder.map(month =>
                              expandedMonths[month]
                                ? olderByMonth[month].map((dia, idx) => (
                                    <React.Fragment key={`dem-${dia.data}`}>
                                      <TableCell className={`text-center px-1 ${idx % 2 === 0 ? 'bg-rose-50/20' : 'bg-rose-50/40'}`}>{zero}</TableCell>
                                      <TableCell className={`text-center px-1 ${idx % 2 === 0 ? 'bg-rose-50/20' : 'bg-rose-50/40'}`}>{zero}</TableCell>
                                      <TableCell className={`text-center px-1 border-r ${idx % 2 === 0 ? 'bg-rose-50/20' : 'bg-rose-50/40'}`}>{zero}</TableCell>
                                    </React.Fragment>
                                  ))
                                : <TableCell key={`dem-col-${month}`} className="border-r bg-rose-50/20 p-0" style={{width:'28px', minWidth:'28px', maxWidth:'28px'}} />
                            )}
                            {recentDias.map((dia, idx) => {
                              // Soma AR/A/F das sub-linhas para o MESMO dia.
                              // Antes, F mostrava o total histórico tratado (incluindo dias passados),
                              // o que desbatia da soma das sub-linhas. Agora bate com cada coluna.
                              let sumAR = 0, sumA = 0, sumF = 0;
                              for (const atv of atividadesRows) {
                                const rb = rowRolling(atv, dia.data);
                                sumAR += rb.ar;
                                sumA  += rb.a;
                                sumF  += rb.f;
                              }
                              return (
                                <React.Fragment key={`dem-r-${dia.data}`}>
                                  <TableCell className={`text-center px-1 ${idx % 2 === 0 ? 'bg-rose-50/20' : 'bg-rose-50/40'}`}>
                                    {sumAR > 0 ? <span className="text-sm font-bold text-yellow-700">{sumAR}</span> : zero}
                                  </TableCell>
                                  <TableCell className={`text-center px-1 ${idx % 2 === 0 ? 'bg-rose-50/20' : 'bg-rose-50/40'}`}>
                                    {sumA > 0 ? <span className="text-sm font-bold text-orange-700">{sumA}</span> : zero}
                                  </TableCell>
                                  <TableCell className={`text-center px-1 border-r ${idx % 2 === 0 ? 'bg-rose-50/20' : 'bg-rose-50/40'}`}>
                                    {sumF > 0 ? <span className="text-sm font-bold text-emerald-700">{sumF}</span> : zero}
                                  </TableCell>
                                </React.Fragment>
                              );
                            })}
                            <TableCell className="text-center px-1 bg-indigo-100/50 border-r">
                              <span className="font-bold text-indigo-700">{demPendente || 0}</span>
                            </TableCell>
                          </TableRow>

                          {/* Sub-linhas de atividades */}
                          {atividadesRows.map(atv => {
                            const isOk = !!canalChecks[atv.key];
                            return (
                              <TableRow key={atv.key}
                                className={`${isOk ? 'bg-emerald-50 dark:bg-emerald-950/30 hover:bg-emerald-100/70' : 'bg-amber-50/40 hover:bg-amber-50/70'}`}>
                                <TableCell className="border-r text-sm pl-3">
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      title={isOk ? `OK marcado por ${canalChecks[atv.key]?.marcado_por} — clique para desmarcar` : 'Marcar como OK'}
                                      onClick={() => toggleCanalCheck(atv.key)}
                                      className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center text-sm font-bold transition-all ${
                                        isOk
                                          ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600'
                                          : 'border-slate-300 text-slate-300 hover:border-emerald-400 hover:text-emerald-400'
                                      }`}
                                    >✓</button>
                                    <span
                                      className={`text-sm cursor-pointer hover:underline ${isOk ? 'text-emerald-700 font-semibold' : 'text-slate-600'}`}
                                      onClick={() => navigate(atv.path)}
                                    >{atv.label}</span>
                                  </div>
                                </TableCell>
                                {olderMonthsOrder.map(month =>
                                  expandedMonths[month]
                                    ? olderByMonth[month].map((dia, idx) => {
                                        // Saldo rolante (AR=criados, A=saldo abertura, F=encerrados/resolvidos)
                                        const rb = rowRolling(atv, dia.data);
                                        return (
                                          <React.Fragment key={`${atv.key}-${dia.data}`}>
                                            <TableCell className={`text-center px-1 ${idx % 2 === 0 ? 'bg-amber-50/20' : 'bg-amber-50/40'}`}>
                                              {rb.ar > 0 ? <span className="text-sm font-semibold text-yellow-700">{rb.ar}</span> : zero}
                                            </TableCell>
                                            <TableCell className={`text-center px-1 ${idx % 2 === 0 ? 'bg-amber-50/20' : 'bg-amber-50/40'}`}>
                                              {rb.a > 0 ? <span className="text-sm font-semibold text-orange-700">{rb.a}</span> : zero}
                                            </TableCell>
                                            <TableCell className={`text-center px-1 border-r ${idx % 2 === 0 ? 'bg-amber-50/20' : 'bg-amber-50/40'}`}>
                                              {rb.f > 0 ? <span className="text-sm font-semibold text-emerald-700">{rb.f}</span> : zero}
                                            </TableCell>
                                          </React.Fragment>
                                        );
                                      })
                                    : <TableCell key={`${atv.key}-col-${month}`} className="border-r bg-amber-50/20 p-0" style={{width:'28px', minWidth:'28px', maxWidth:'28px'}} />
                                )}
                                {recentDias.map((dia, idx) => {
                                  // Saldo rolante para todas as linhas de atividade:
                                  //   AR = criados no dia | A = saldo de abertura | F = encerrados/resolvidos no dia
                                  const rb = rowRolling(atv, dia.data);
                                  return (
                                    <React.Fragment key={`${atv.key}-r-${dia.data}`}>
                                      <TableCell className={`text-center px-1 ${idx % 2 === 0 ? 'bg-amber-50/20' : 'bg-amber-50/40'}`}>
                                        {rb.ar > 0 ? <span className="text-sm font-semibold text-yellow-700">{rb.ar}</span> : zero}
                                      </TableCell>
                                      <TableCell className={`text-center px-1 ${idx % 2 === 0 ? 'bg-amber-50/20' : 'bg-amber-50/40'}`}>
                                        {rb.a > 0 ? <span className="text-sm font-semibold text-orange-700">{rb.a}</span> : zero}
                                      </TableCell>
                                      <TableCell className={`text-center px-1 border-r ${idx % 2 === 0 ? 'bg-amber-50/20' : 'bg-amber-50/40'}`}>
                                        {rb.f > 0 ? <span className="text-sm font-semibold text-emerald-700">{rb.f}</span> : zero}
                                      </TableCell>
                                    </React.Fragment>
                                  );
                                })}
                                <TableCell className="text-center px-1 bg-indigo-100/30 border-r">
                                  <span className={`text-sm ${atv.pendente > 0 ? 'font-semibold text-indigo-700' : 'text-muted-foreground'}`}>{atv.pendente || 0}</span>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </>
                      );
                    })()}
                    {(!visaoGeral?.por_canal_dia || visaoGeral.por_canal_dia.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={RECENT_DAYS * 4 + olderMonthsOrder.length + 2} className="text-center text-muted-foreground py-8">
                          Nenhum dado disponível
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* ── Verificação de status (limpeza por motivo de pendência) ── */}
      {verifRows.length > 0 && (() => {
        const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
        const hoje = verifHoje || fmtLocal(new Date());
        // Check ✓ "feito hoje" (igual ao dash): verde quando última = hoje
        const checkBtn = (motivo, parceiro, ultima, small) => {
          const ok = !!ultima && ultima === hoje;
          return (
            <button type="button"
              onClick={() => toggleVerifCheck(motivo, parceiro)}
              title={ok ? 'Verificado hoje — clique para desfazer' : 'Marcar verificado hoje (carimba última e avança próxima)'}
              className={`flex-shrink-0 ${small ? 'w-4 h-4 text-[9px]' : 'w-5 h-5 text-[11px]'} rounded-full border-2 flex items-center justify-center font-bold transition-all ${
                ok ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600'
                   : 'border-slate-300 text-slate-300 hover:border-emerald-400 hover:text-emerald-400'}`}
            >✓</button>
          );
        };
        // Célula de data (última/próxima) — editável; próxima fica vermelha + "• hoje" quando devida
        const dateCell = (motivo, parceiro, field, val, opts) => {
          const cor = (opts && opts.devida) ? 'text-red-600 font-semibold' : 'text-slate-500';
          const dia = val ? DIAS[new Date(val + 'T12:00:00').getDay()] : '';
          return (
            <span className="inline-flex items-center gap-1.5">
              <input type="date" value={val || ''}
                onChange={(e) => salvarVerifCampo(motivo, parceiro, { [field]: e.target.value })}
                title="Ajuste manual"
                className={`bg-transparent border-0 p-0 text-sm cursor-pointer focus:outline-none ${cor}`}
                style={{ width: val ? '92px' : '20px' }} />
              {val && <span className={`text-xs ${cor}`}>({dia})</span>}
              {opts && opts.atraso && <span className="text-xs text-red-600 font-bold">• atrasada</span>}
              {opts && opts.flagHoje && <span className="text-xs text-red-600 font-semibold">• hoje</span>}
            </span>
          );
        };
        // Célula "Observação" (salva no blur)
        const obsCell = (motivo, parceiro, obs, small) => (
          <input type="text" value={obs || ''} placeholder="anotação…"
            onChange={(e) => updateVerifLocal(motivo, parceiro, { obs: e.target.value })}
            onBlur={(e) => postVerif(motivo, parceiro, { obs: e.target.value })}
            className={`w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-300 dark:hover:border-slate-700 rounded px-2 ${small ? 'py-0.5' : 'py-1'} text-sm focus:outline-none`} />
        );
        // "O que fazer hoje" (rotina de 17/10/2026): no dia de acionar, a ação em laranja;
        // nos outros dias, só a conferência. Entregue mostra quem deixou assim.
        const fazerCell = (r, rot, hojeAc) => {
          const botao = rot.lista && (
            <button type="button"
              onClick={() => (rot.lista === 'camila' ? relatorioCamila() : setCobranca(rot.lista))}
              className={`mt-1 self-start text-xs font-semibold rounded border px-2 py-0.5 ${hojeAc
                ? 'border-orange-400 text-orange-700 bg-white hover:bg-orange-50 dark:bg-transparent dark:text-orange-300'
                : 'border-slate-300 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
              {rot.lista === 'camila' ? 'Relatório da Camila' : `Lista da ${rot.lista}`}
            </button>
          );
          return (
            <div className="flex flex-col text-sm leading-snug">
              {rot.encerrar ? (
                <>
                  <span className={`font-semibold ${r.qtd > 0 ? 'text-red-700 dark:text-red-300' : 'text-muted-foreground'}`}>{r.qtd > 0 ? rot.acao : rot.conf}</span>
                  {(r.donos || []).length > 0 && (
                    <span className="text-muted-foreground">deixados por {(r.donos || []).map(d => `${d.atendente} (${d.qtd})`).join(' · ')}</span>
                  )}
                </>
              ) : hojeAc ? (
                <>
                  <span className="font-semibold text-orange-700 dark:text-orange-300">{rot.acao}</span>
                  {!['Conferir', 'Dar uma olhada'].includes(rot.conf) && <span className="text-muted-foreground">e {rot.conf.toLowerCase()}</span>}
                </>
              ) : (
                <>
                  <span className="text-slate-600 dark:text-slate-300">{rot.conf}</span>
                  {rot.nao && <span className="text-xs text-muted-foreground">{rot.nao}</span>}
                </>
              )}
              {botao}
              {rot.portal && linkPortal(rot.portal)}
              {rot.nota && <span className="text-xs text-muted-foreground mt-0.5">{rot.nota}</span>}
            </div>
          );
        };
        const proxAcionarCell = (motivo, hojeAc) => {
          const dias = diasAcionar(motivo);
          if (!dias.length) return <span className="text-muted-foreground">não aciona</span>;
          const prox = proximoAcionamento(dias);
          return hojeAc
            ? <span><span className="font-semibold text-orange-700 dark:text-orange-300">hoje</span>{prox ? <span className="text-muted-foreground"> · depois {prox}</span> : null}</span>
            : <span>{prox || '?'}</span>;
        };
        return (
        <Card className="mt-4">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 flex-wrap">
              <span>🔎 Verificação de status</span>
              <span className="text-xs font-normal text-muted-foreground">
                todo status é conferido todo dia útil · a coluna "O que fazer hoje" diz quando acionar alguém
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/30 border-y">
                <tr>
                  <th className="text-center px-2 py-2 font-semibold w-10">✓</th>
                  <th className="text-left px-2 py-2 font-semibold w-60">Status</th>
                  <th className="text-center px-3 py-2 font-semibold w-20">Em aberto</th>
                  <th className="text-left px-3 py-2 font-semibold min-w-[220px]">O que fazer hoje</th>
                  <th className="text-left px-3 py-2 font-semibold w-36">Próximo acionamento</th>
                  <th className="text-left px-3 py-2 font-semibold w-32">Última verif.</th>
                  <th className="text-left px-3 py-2 font-semibold">Observação</th>
                </tr>
              </thead>
              <tbody>
                {verifRows.map(r => {
                  const temSub = Array.isArray(r.sub);
                  const aberto = !!verifExpand[r.motivo];
                  const flags = flagsVerif(r.proxima || '', r.ultima || '', r.motivo, hoje);
                  const rot = rotinaDe(r.motivo);
                  const hojeAc = acionarHoje(r.motivo);
                  const corLinha = rot.encerrar && r.qtd > 0 ? 'bg-red-50/70 dark:bg-red-950/20'
                    : (hojeAc && (r.qtd > 0 || r.canc_aes > 0) ? 'bg-orange-50/70 dark:bg-orange-950/20' : '');
                  return (
                    <React.Fragment key={r.motivo}>
                      <tr className={`border-b last:border-0 hover:bg-muted/40 ${corLinha}`}>
                        <td className="px-2 py-2 text-center">{checkBtn(r.motivo, '', r.ultima, false)}</td>
                        <td className="px-2 py-2 font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            {temSub ? (
                              <button type="button"
                                onClick={() => setVerifExpand(p => ({ ...p, [r.motivo]: !p[r.motivo] }))}
                                title={aberto ? 'Recolher parceiros' : 'Abrir por parceiro'}
                                className="w-4 h-4 flex items-center justify-center rounded border text-sm leading-none text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700">
                                {aberto ? '−' : '+'}
                              </button>
                            ) : <span className="inline-block w-4" />}
                            {r.motivo}
                            {donoChip(r.motivo)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={r.qtd > 0 ? 'font-bold text-indigo-700 dark:text-indigo-400' : 'text-muted-foreground'}>{r.qtd}</span>
                          {r.canc_aes > 0 && (
                            <span className="block text-[11px] font-semibold text-purple-700 dark:text-purple-300 whitespace-nowrap"
                              title="Cancelamentos do AES com o ticket do canal aberto, esperando o parceiro">
                              + {r.canc_aes} cancel. AES
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">{fazerCell(r, rot, hojeAc)}</td>
                        <td className="px-3 py-2 text-sm whitespace-nowrap">{proxAcionarCell(r.motivo, hojeAc)}</td>
                        <td className="px-3 py-2">
                          {dateCell(r.motivo, '', 'ultima', r.ultima || '', {})}
                          {botaoHist(r.motivo)}
                          {flags.atraso && <span className="ml-1 text-xs text-red-600 font-bold">• atrasada</span>}
                          {flags.flagHoje && <span className="ml-1 text-xs text-red-600 font-semibold">• hoje</span>}
                        </td>
                        <td className="px-3 py-2">{obsCell(r.motivo, '', r.obs, false)}</td>
                      </tr>
                      {temSub && aberto && r.sub.map(s => {
                        const subFlags = flagsVerif(s.proxima || '', s.ultima || '', r.motivo, hoje);
                        return (
                        <tr key={r.motivo + '::' + s.parceiro} className="border-b last:border-0 bg-slate-50/40 dark:bg-slate-800/10">
                          <td className="px-2 py-1.5 text-center">{checkBtn(r.motivo, s.parceiro, s.ultima, true)}</td>
                          <td className="px-2 py-1.5 pl-8 text-[13px] text-slate-600 dark:text-slate-300">↳ {s.parceiro}</td>
                          <td className="px-3 py-1.5 text-center">
                            <span className={s.qtd > 0 ? 'font-semibold text-indigo-600 dark:text-indigo-400' : 'text-muted-foreground'}>{s.qtd}</span>
                            {/* Cancelamentos do AES esperando este parceiro (Adnéia, 21/10/2026): o link
                                abre a tela Cancelamentos já filtrada pelo canal e pelo ticket aberto. */}
                            {s.canc_aes > 0 && s.parceiro === '(sem parceiro)' && (
                              <span className="block text-[11px] font-semibold text-purple-700 dark:text-purple-300 whitespace-nowrap"
                                title="Cancelamentos do AES com ticket aberto, sem o canal no cadastro do pedido">
                                + {s.canc_aes} cancel. AES
                              </span>
                            )}
                            {s.canc_aes > 0 && s.parceiro !== '(sem parceiro)' && (
                              <button type="button"
                                onClick={() => navigate(`/cancelamentos?canal=${encodeURIComponent(s.parceiro)}&ticket=aberto`)}
                                title={`Abrir os ${s.canc_aes} cancelamento(s) do AES com ticket aberto na ${s.parceiro}`}
                                className="block mx-auto text-[11px] font-semibold text-purple-700 hover:underline dark:text-purple-300 whitespace-nowrap">
                                + {s.canc_aes} cancel. AES
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-[12px]">
                            <div className="flex flex-col">
                              {hojeAc && (s.qtd > 0 || s.canc_aes > 0)
                                ? <span className="font-semibold text-orange-700 dark:text-orange-300">Cobrar {s.parceiro}</span>
                                : <span className="text-muted-foreground">conferir</span>}
                              {linkPortal(s.parceiro, 'portal')}
                            </div>
                          </td>
                          <td className="px-3 py-1.5" />
                          <td className="px-3 py-1.5">
                            {dateCell(r.motivo, s.parceiro, 'ultima', s.ultima || '', {})}
                            {botaoHist(r.motivo, s.parceiro)}
                            {subFlags.atraso && <span className="ml-1 text-xs text-red-600 font-bold">• atrasada</span>}
                          </td>
                          <td className="px-3 py-1.5">{obsCell(r.motivo, s.parceiro, s.obs, true)}</td>
                        </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
        );
      })()}

      {/* ── Verificação de atividades (todas diárias em dias úteis) ── */}
      {ativRows.length > 0 && (() => {
        const hoje = ativHoje || fmtLocal(new Date());
        // Atividade com perguntas (Cancelamentos (AES), 21/10/2026): o ✓ abre a janela; verde
        // carimba o Feito hoje, amarelo não carimba até virar verde (o servidor decide).
        const chaveAtiv = (motivo) => Object.keys(checklists).find(k => k.startsWith('atividade:') && checklists[k]?.rotulo === motivo);
        const checkBtn = (motivo, ultima) => {
          const ok = !!ultima && ultima === hoje;
          const chave = chaveAtiv(motivo);
          const pend = !!chave && canalChecks[chave]?.situacao === 'pendencia';
          return (
            <button type="button" onClick={() => (chave ? setCheckCanalAberto(chave) : toggleAtivCheck(motivo))}
              title={chave
                ? (canalChecks[chave] ? `${resumoDoCheck(canalChecks[chave], checklists[chave])}\n\nClique para responder de novo ou desmarcar` : 'Responder as perguntas e marcar')
                : (ok ? 'Verificado hoje — clique para desfazer' : 'Marcar verificado hoje (carimba última e avança próxima)')}
              className={`flex-shrink-0 w-5 h-5 text-[11px] rounded-full border-2 flex items-center justify-center font-bold transition-all ${pend
                ? 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600'
                : ok ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600' : 'border-slate-300 text-slate-300 hover:border-emerald-400 hover:text-emerald-400'}`}
            >{pend ? '!' : '✓'}</button>
          );
        };
        // Campo de lançamento. A key inclui o valor do servidor pra o input
        // remontar quando a resposta trouxer outro número.
        const campoSaldo = (atividade, l, nome) => (
          <input
            type="number" min="0" defaultValue={l[nome] ?? 0}
            key={atividade + l.transportadora + nome + (l[nome] ?? 0)}
            disabled={saldoSalvando === atividade + l.transportadora + nome}
            onBlur={e => salvarSaldo(atividade, l.transportadora, nome, e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
            className="w-16 text-center border rounded px-1 py-0.5 text-[13px] bg-white dark:bg-slate-900 disabled:opacity-50"
          />
        );
        const obsCell = (motivo, obs) => (
          <input type="text" value={obs || ''} placeholder="anotação…"
            onChange={(e) => updateAtivLocal(motivo, { obs: e.target.value })}
            onBlur={(e) => postVerif(motivo, '', { obs: e.target.value })}
            className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-300 dark:hover:border-slate-700 rounded px-2 py-1 text-sm focus:outline-none" />
        );
        // "Feito hoje" (Adnéia, 17/10/2026, decisão 7): quem marcou o ✓ e a hora. Sem ✓
        // hoje, o aviso; atrasada quando nem ontem foi feito.
        const feitoHojeCell = (r, flags) => {
          const chave = chaveAtiv(r.motivo);
          const ckPend = chave && canalChecks[chave]?.situacao === 'pendencia' ? canalChecks[chave] : null;
          if (ckPend) {
            let hora = '';
            try { hora = ckPend.marcado_em ? new Date(ckPend.marcado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''; } catch {}
            return (
              <span className="font-semibold text-amber-700 dark:text-amber-300" title={`Feito com pendência: não conta como feito até virar verde.\nFicou: ${ckPend.o_que_ficou || '?'}`}>
                ! com pendência · {ckPend.marcado_por || '?'}{hora ? ` · ${hora}` : ''}
              </span>
            );
          }
          if (r.ultima && r.ultima === hoje) {
            const por = r.check_por || r.atualizado_por || '';
            let hora = '';
            try { hora = r.check_em ? new Date(r.check_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''; } catch {}
            // Verde com 🔔 (Tem em Casa, 23/10/2026): a pessoa conferiu algo para a Adnéia decidir.
            const ckAviso = chave && canalChecks[chave]?.avisos?.length ? canalChecks[chave] : null;
            return (
              <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                ✓ {por}{hora ? ` · ${hora}` : ''}
                {ckAviso && <span className="ml-1 cursor-help" title={`Aviso para a Adnéia: ${ckAviso.avisos.join(', ')}\n${ckAviso.o_que_ficou || ''}`}>🔔</span>}
              </span>
            );
          }
          // Fim de semana e feriado não cobram: mostra só a última vez.
          if (!flags.devida) return <span className="text-muted-foreground">{r.ultima ? `última em ${fmtBR(r.ultima)}` : '—'}</span>;
          return (
            <span className="font-semibold text-red-600">
              falta hoje{flags.atraso && r.ultima ? <span className="font-normal text-red-500"> · última em {fmtBR(r.ultima)}</span> : null}
            </span>
          );
        };
        return (
        <Card className="mt-4">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 flex-wrap">
              <span>🗂️ Verificação de atividades</span>
              <span className="text-xs font-normal text-muted-foreground">
                todas todo dia útil · a próxima é sempre o dia útil seguinte
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/30 border-y">
                <tr>
                  <th className="text-center px-2 py-2 font-semibold w-10">✓</th>
                  <th className="text-left px-2 py-2 font-semibold w-60">Atividade</th>
                  <th className="text-center px-3 py-2 font-semibold w-20">Em aberto</th>
                  <th className="text-left px-3 py-2 font-semibold w-48">Feito hoje</th>
                  <th className="text-left px-3 py-2 font-semibold">Observação</th>
                </tr>
              </thead>
              <tbody>
                {ativRows.map(r => {
                  // Tem em Casa e Avisos de Compras vêm contados do backend
                  // (o Dashboard não carrega esses dados); o resto conta aqui.
                  const qtd = qtdAtivDe(r);
                  const flags = flagsVerif(r.proxima || '', r.ultima || '', r.motivo, hoje);
                  const linha = (
                    <tr key={r.motivo} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="px-2 py-2 text-center">{checkBtn(r.motivo, r.ultima)}</td>
                      <td className="px-2 py-2 font-medium">
                        {/* O "+" abre entrou/saiu numa sub-linha em TODA atividade de saldo —
                            inclusive as de item único (Ocorrência GRS, Atendimento), como a de transportadora (Adnéia, 06/10). */}
                        {saldoAtivs[r.motivo] ? (
                          <button type="button"
                            onClick={() => setSaldoAberto(p => ({ ...p, [r.motivo]: !p[r.motivo] }))}
                            title={saldoAberto[r.motivo] ? 'Recolher' : 'Abrir para lançar entrou e saiu'}
                            className="w-4 h-4 mr-1 inline-flex items-center justify-center rounded border text-sm leading-none text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700">
                            {saldoAberto[r.motivo] ? '−' : '+'}
                          </button>
                        ) : Array.isArray(r.sub) ? (
                          <button type="button" onClick={() => setImportarAberto(a => !a)}
                            title={importarAberto ? 'Recolher' : 'Abrir uma linha por base'}
                            className="w-4 h-4 mr-1 inline-flex items-center justify-center rounded border text-sm leading-none text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700">
                            {importarAberto ? '−' : '+'}
                          </button>
                        ) : <span className="inline-block w-4" />}
                        {r.motivo}
                        {Array.isArray(r.sub) && (
                          <button type="button" onClick={() => navigate('/importar')}
                            className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 dark:text-blue-300 hover:underline">
                            <Upload className="h-3 w-3" /> Abrir Base ELO
                          </button>
                        )}
                        {manuais[r.motivo] && (
                          <button type="button" onClick={() => setManualAberto(r.motivo)}
                            title={`Como fazer: o manual do ${r.motivo}`} aria-label={`Como fazer: o manual do ${r.motivo}`}
                            className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-blue-400 text-[10px] font-bold leading-none text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/40 align-middle">
                            ?
                          </button>
                        )}
                        {' '}{donoChip(r.motivo)}
                        {/* Respostas do check da atividade, últimos 30 dias: só a gestão (como nos canais) */}
                        {chaveAtiv(r.motivo) && user?.role === 'admin' && (
                          <button type="button" onClick={() => setRespostasCanal(chaveAtiv(r.motivo))}
                            title={`Respostas do check de ${r.motivo}, últimos 30 dias`}
                            className="ml-2 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-muted dark:text-slate-300 align-middle">
                            <History className="h-3 w-3" /> Respostas
                          </button>
                        )}
                        {/* A atividade é feita na tela do ELO, não mais na planilha do SharePoint (Adnéia, 29/10/2026) */}
                        {r.motivo === 'Pendência de Transportadora' && (
                          <button type="button" onClick={() => navigate(TELA_PEND_TRANSP)}
                            className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 dark:text-blue-300 hover:underline">
                            <Truck className="h-3 w-3" /> Abrir tela
                          </button>
                        )}
                        {/* O Atendimento é feito na planilha Cancelamento Geral do SharePoint (Adnéia, 04/11/2026) */}
                        {r.motivo === 'Atendimento' && (
                          <a href={PLANILHA_CANCELAMENTO_GERAL} target="_blank" rel="noopener noreferrer"
                            className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 dark:text-blue-300 hover:underline">
                            <ExternalLink className="h-3 w-3" /> Abrir planilha
                          </a>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {/* O que o número conta vem do backend: "Em aberto"
                            não quer dizer a mesma coisa em toda linha. */}
                        <span
                          title={r.em_aberto_desc || undefined}
                          className={qtd > 0 ? 'font-bold text-indigo-700 dark:text-indigo-400' : 'text-muted-foreground'}
                        >{qtd}</span>
                      </td>
                      <td className="px-3 py-2 text-sm">{feitoHojeCell(r, flags)}{botaoHist(r.motivo)}</td>
                      <td className="px-3 py-2">{obsCell(r.motivo, r.obs)}</td>
                    </tr>
                  );
                  // Importar dados: uma sub-linha por base, com ✓, observação e o botão de subir.
                  if (Array.isArray(r.sub)) {
                    if (!importarAberto) return linha;
                    return (
                      <React.Fragment key={r.motivo}>
                        {linha}
                        {r.sub.map(s => {
                          const ok = !!s.ultima && s.ultima === hoje;
                          let hora = '';
                          try { hora = s.check_em ? new Date(s.check_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''; } catch {}
                          return (
                            <tr key={'base::' + s.parceiro} className="border-b last:border-0 bg-slate-50/40 dark:bg-slate-800/10">
                              <td className="px-2 py-1.5 text-center">
                                <button type="button" onClick={() => toggleAtivSubCheck(r.motivo, s.parceiro)}
                                  title={ok ? 'Feito hoje — clique para desfazer' : 'Marcar feito hoje'}
                                  className={`mx-auto w-5 h-5 text-[11px] rounded-full border-2 flex items-center justify-center font-bold transition-all ${ok
                                    ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600'
                                    : 'border-slate-300 text-slate-300 hover:border-emerald-400 hover:text-emerald-400'}`}>✓</button>
                              </td>
                              <td className="px-2 py-1.5 pl-8 text-[13px] text-slate-600 dark:text-slate-300">
                                ↳ {s.parceiro}
                                <label className={`ml-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 dark:text-blue-300 ${subindoBase ? 'opacity-50' : 'cursor-pointer hover:underline'}`}
                                  title={`Escolher o arquivo do ${s.parceiro} e subir agora`}>
                                  <Upload className="h-3 w-3" /> {subindoBase === s.parceiro ? 'Subindo…' : 'Importar'}
                                  <input type="file" className="hidden" disabled={!!subindoBase}
                                    onChange={e => subirBase(s.parceiro, e)} />
                                </label>
                              </td>
                              <td className="px-3 py-1.5" />
                              <td className="px-3 py-1.5 text-[12px]">
                                {ok
                                  ? <span className="font-semibold text-emerald-700 dark:text-emerald-300">✓ {s.check_por}{hora ? ` · ${hora}` : ''}</span>
                                  : <span className="font-semibold text-red-600">falta hoje</span>}
                              </td>
                              <td className="px-3 py-1.5">
                                <input type="text" value={s.obs || ''} placeholder="anotação… (ex.: portal Total fora)"
                                  onChange={(e) => updateAtivSub(r.motivo, s.parceiro, { obs: e.target.value })}
                                  onBlur={(e) => postVerif(r.motivo, s.parceiro, { obs: e.target.value })}
                                  className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-300 dark:hover:border-slate-700 rounded px-2 py-1 text-sm focus:outline-none" />
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  }
                  const dados = saldoAtivs[r.motivo];
                  if (!dados || !saldoAberto[r.motivo]) return linha;
                  // Sub-linhas: uma por item, com os dois campos do dia. O 'aberto'
                  // vem do servidor e é só leitura de propósito.
                  return (
                    <React.Fragment key={r.motivo}>
                      {linha}
                      {(dados.linhas || []).map(l => (
                        <tr key={'saldo::' + r.motivo + '::' + l.transportadora} className="border-b last:border-0 bg-slate-50/40 dark:bg-slate-800/10">
                          <td className="px-2 py-1.5" />
                          <td className="px-2 py-1.5 pl-8 text-[13px] text-slate-600 dark:text-slate-300">↳ {dados.item_unico ? 'lançamento do dia' : l.transportadora}</td>
                          <td className="px-3 py-1.5 text-center">
                            <span className={l.aberto > 0 ? 'font-semibold text-indigo-600 dark:text-indigo-400' : 'text-muted-foreground'}>{l.aberto}</span>
                          </td>
                          <td className="px-3 py-1.5 text-[12px] text-slate-500" colSpan={2}>
                            <span className="inline-flex items-center gap-1.5 mr-4">entrou {campoSaldo(r.motivo, l, 'entrou')}</span>
                            <span className="inline-flex items-center gap-1.5">saiu {campoSaldo(r.motivo, l, 'saiu')}</span>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
        );
      })()}

      <div className="hidden">
        <div className="hidden">

          {/* ── Disponível para Retirada ── */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
                <Package className="h-4 w-4" />
                <span className="font-semibold text-sm">Disp. p/ Retirada</span>
              </div>
              <div className="flex gap-3 text-sm font-medium">
                <span className="text-amber-600">{(retiradaData || []).filter(p => !p.status_final).length} pendentes</span>
                <span className="text-emerald-600">{(retiradaData || []).filter(p => !!p.status_final).length} tratados</span>
              </div>
            </div>

            {/* Pendentes retirada */}
            {(retiradaData || []).filter(p => !p.status_final).length > 0 && (
              <Card className="border-amber-100">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-sm font-semibold text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Necessário Tratar
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-amber-50 dark:bg-amber-950/20 border-b">
                      <tr>
                        <th className="text-left px-3 py-1.5 font-semibold text-amber-700">Nota</th>
                        <th className="text-left px-3 py-1.5 font-semibold text-amber-700">Cliente</th>
                        <th className="text-left px-3 py-1.5 font-semibold text-amber-700">Acionamento</th>
                        <th className="text-right px-3 py-1.5 font-semibold text-amber-700">Dias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(retiradaData || []).filter(p => !p.status_final).map(p => {
                        const dias = p.dias_disponivel;
                        const urgente = typeof dias === 'number' && dias >= 7;
                        return (
                          <tr key={p.nota_fiscal} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => navigate('/retirada')}>
                            <td className="px-3 py-1.5 font-mono font-semibold">{p.nota_fiscal}</td>
                            <td className="px-3 py-1.5 max-w-[120px] truncate">{p.nome_cliente || '—'}</td>
                            <td className="px-3 py-1.5">{p.acionamento || '—'}</td>
                            <td className="px-3 py-1.5 text-right font-bold">
                              <span className={urgente ? 'text-red-600' : 'text-amber-600'}>
                                {dias != null ? `${dias}d` : '—'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Tratados retirada */}
            {(retiradaData || []).filter(p => !!p.status_final).length > 0 && (
              <Card className="border-emerald-100">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-sm font-semibold text-emerald-600 flex items-center gap-1">
                    <CheckCircle className="h-3.5 w-3.5" /> Tratados
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-emerald-50 dark:bg-emerald-950/20 border-b">
                      <tr>
                        <th className="text-left px-3 py-1.5 font-semibold text-emerald-700">Nota</th>
                        <th className="text-left px-3 py-1.5 font-semibold text-emerald-700">Cliente</th>
                        <th className="text-left px-3 py-1.5 font-semibold text-emerald-700">Status Final</th>
                        <th className="text-left px-3 py-1.5 font-semibold text-emerald-700">Por</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(retiradaData || []).filter(p => !!p.status_final).map(p => (
                        <tr key={p.nota_fiscal} className="border-b last:border-0 bg-emerald-50/30 hover:bg-emerald-50 cursor-pointer" onClick={() => navigate('/retirada')}>
                          <td className="px-3 py-1.5 font-mono font-semibold">{p.nota_fiscal}</td>
                          <td className="px-3 py-1.5 max-w-[120px] truncate">{p.nome_cliente || '—'}</td>
                          <td className="px-3 py-1.5">
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[11px] px-1.5">{p.status_final}</Badge>
                          </td>
                          <td className="px-3 py-1.5 text-slate-500">{p.finalizado_por || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {!retiradaData && loadingAtividades && (
              <div className="text-center py-4 text-sm text-muted-foreground">Carregando...</div>
            )}
            {retiradaData && retiradaData.length === 0 && (
              <div className="text-center py-4 text-sm text-muted-foreground">Nenhum pedido disponível para retirada</div>
            )}
          </div>

          {/* ── Pagamento Não Aprovado ── */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
                <FileText className="h-4 w-4" />
                <span className="font-semibold text-sm">Pag. Não Aprovado</span>
              </div>
              <div className="flex gap-3 text-sm font-medium">
                <span className="text-amber-600">{(pagamentoData || []).filter(p => !p.status_final).length} pendentes</span>
                {(pagamentoData || []).filter(p => !p.status_final && (p.dias_no_status ?? 0) >= 7).length > 0 && (
                  <span className="text-red-600 font-bold">⚠ {(pagamentoData || []).filter(p => !p.status_final && (p.dias_no_status ?? 0) >= 7).length} urgentes</span>
                )}
                <span className="text-emerald-600">{(pagamentoData || []).filter(p => !!p.status_final).length} tratados</span>
              </div>
            </div>

            {/* Pendentes pagamento */}
            {(pagamentoData || []).filter(p => !p.status_final).length > 0 && (
              <Card className="border-amber-100">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-sm font-semibold text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Necessário Tratar
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-amber-50 dark:bg-amber-950/20 border-b">
                      <tr>
                        <th className="text-left px-3 py-1.5 font-semibold text-amber-700">Pedido</th>
                        <th className="text-left px-3 py-1.5 font-semibold text-amber-700">Cliente</th>
                        <th className="text-left px-3 py-1.5 font-semibold text-amber-700">CPF</th>
                        <th className="text-right px-3 py-1.5 font-semibold text-amber-700">Dias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(pagamentoData || []).filter(p => !p.status_final).map(p => {
                        const urgente = (p.dias_no_status ?? 0) >= 7;
                        return (
                          <tr key={p.numero_pedido} className={`border-b last:border-0 cursor-pointer hover:bg-muted/30 ${urgente ? 'bg-red-50/40' : ''}`} onClick={() => navigate('/pagamento')}>
                            <td className="px-3 py-1.5 font-mono font-semibold">{p.numero_pedido}</td>
                            <td className="px-3 py-1.5 max-w-[120px] truncate">{p.nome_cliente || '—'}</td>
                            <td className="px-3 py-1.5 font-mono">{p.cpf_cliente || '—'}</td>
                            <td className="px-3 py-1.5 text-right font-bold">
                              <span className={urgente ? 'text-red-600' : 'text-amber-600'}>
                                {p.dias_no_status != null ? `${p.dias_no_status}d` : '—'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Tratados pagamento */}
            {(pagamentoData || []).filter(p => !!p.status_final).length > 0 && (
              <Card className="border-emerald-100">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-sm font-semibold text-emerald-600 flex items-center gap-1">
                    <CheckCircle className="h-3.5 w-3.5" /> Cancelados
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-emerald-50 dark:bg-emerald-950/20 border-b">
                      <tr>
                        <th className="text-left px-3 py-1.5 font-semibold text-emerald-700">Pedido</th>
                        <th className="text-left px-3 py-1.5 font-semibold text-emerald-700">Cliente</th>
                        <th className="text-left px-3 py-1.5 font-semibold text-emerald-700">Instância</th>
                        <th className="text-left px-3 py-1.5 font-semibold text-emerald-700">Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(pagamentoData || []).filter(p => !!p.status_final).map(p => (
                        <tr key={p.numero_pedido} className="border-b last:border-0 bg-emerald-50/30 hover:bg-emerald-50 cursor-pointer" onClick={() => navigate('/pagamento')}>
                          <td className="px-3 py-1.5 font-mono font-semibold">{p.numero_pedido}</td>
                          <td className="px-3 py-1.5 max-w-[120px] truncate">{p.nome_cliente || '—'}</td>
                          <td className="px-3 py-1.5 font-mono font-semibold text-emerald-700">{p.instancia || '—'}</td>
                          <td className="px-3 py-1.5 text-slate-500">{p.data_instancia || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {!pagamentoData && loadingAtividades && (
              <div className="text-center py-4 text-sm text-muted-foreground">Carregando...</div>
            )}
            {pagamentoData && pagamentoData.length === 0 && (
              <div className="text-center py-4 text-sm text-muted-foreground">Nenhum pedido com pagamento não aprovado</div>
            )}
          </div>

        </div>
      </div>

    </div>
  );

  // ABA 2 - VOLUME POR CANAL
  const TabVolumeCanal = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Ranking — ocupa 2/3 da largura */}
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Ranking por Canal</CardTitle>
            <CardDescription>Total: {volumeCanal?.total || 0} atendimentos</CardDescription>
            <p className="text-xs text-muted-foreground" data-testid="formula-canal-demorou">
              Canal &gt; 5 dias úteis: dos atendimentos do canal abertos no período com a data em que o cliente
              acionou o canal, quantos passaram de 5 dias úteis até a abertura no ELO (sem sábado, domingo e
              feriados). Os sem data ficam à parte. Fonte: o card do atendimento, no período do filtro.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {volumeCanal?.ranking?.map((item, idx) => (
              <div key={item.canal} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center font-semibold">
                    {idx + 1}
                  </span>
                  <span className="font-medium">{item.canal}</span>
                </div>
                <div className="flex items-center gap-6">
                  {item.n_pedidos > 0 && (
                    <span className="text-sm text-blue-600 font-medium">
                      {item.pct_vendas}% vendas ({item.n_pedidos.toLocaleString()})
                    </span>
                  )}
                  {(() => {
                    // Cobrança dos canais (Adnéia, 04/11/2026): "?" sem dado, "—" no canal que não entra.
                    const demora = textoColunaDemora(item.cobranca);
                    return (
                      <div className="text-right min-w-[150px]" data-testid={`canal-demorou-${item.canal}`}>
                        <div className="text-xs text-muted-foreground">Canal &gt; 5 dias úteis</div>
                        <div className="text-sm">
                          <span className={`font-semibold ${item.cobranca?.acima ? 'text-red-600 dark:text-red-400' : ''}`}>{demora.principal}</span>
                          <span className="ml-1 text-xs text-muted-foreground">{demora.detalhe}</span>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex items-center gap-2 min-w-[110px] justify-end">
                    <Badge variant="secondary">{item.total}</Badge>
                    <span className="text-sm text-muted-foreground w-16 text-right">{item.percentual}% atend.</span>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Pizza — ocupa 1/3 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Distribuição por Canal</CardTitle>
          </CardHeader>
          <CardContent className="h-[420px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={volumeCanal?.ranking?.slice(0, 8) || []}
                  dataKey="total"
                  nameKey="canal"
                  cx="50%"
                  cy="45%"
                  outerRadius={110}
                  label={({ canal, percentual }) => `${canal}: ${percentual}%`}
                  labelLine={true}
                >
                  {volumeCanal?.ranking?.slice(0, 8).map((_, idx) => (
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      <CancelamentosPeriodo dados={volumeCanal?.cancelamentos} />
    </div>
  );

  // ABA 3 - CLASSIFICAÇÃO
  const TabClassificacao = () => (
    <div className="space-y-6">
      <FalhaComprasFornecedor dados={classificacao?.falha_compras_fornecedor} abertos={fcAbertos}
        onAlternar={(nome) => setFcAbertos(a => ({ ...a, [nome]: !a[nome] }))}
        podeEditar={user?.role === 'admin'} onSalvarModal={salvarModalCombinado} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Por Categoria</CardTitle>
            {classificacao?.total_pedidos > 0 && (
              <CardDescription>% relativo a {classificacao.total_pedidos.toLocaleString()} pedidos</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-2 max-h-80 overflow-y-auto">
            {classificacao?.por_categoria?.map((item) => (
              <div key={item.categoria} className="flex justify-between items-center p-2 rounded bg-muted/30">
                <span className="text-sm">{item.categoria}</span>
                <div className="flex items-center gap-2">
                  <Badge>{item.total}</Badge>
                  {classificacao?.total_pedidos > 0 && (
                    <span className="text-sm text-muted-foreground w-12 text-right">{item.pct_pedidos}%</span>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Pendentes por Categoria</CardTitle>
            <CardDescription>% do total de pendentes | % da categoria</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 max-h-80 overflow-y-auto">
            {classificacao?.pend_categoria?.map((item) => (
              <div key={item.categoria} className="space-y-1">
                <div className="flex justify-between items-center p-2 rounded bg-amber-50 dark:bg-amber-950/30">
                  <span className="text-sm">{item.categoria}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="bg-amber-100 text-amber-800">{item.total}</Badge>
                    <span className="text-sm text-amber-700 font-medium w-10 text-right">{item.pct_pendentes}%</span>
                    <span className="text-sm text-red-600 font-medium w-14 text-right">{item.pct_categoria}% cat.</span>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Por Motivo da Pendência</CardTitle>
            {classificacao?.total_pedidos > 0 && (
              <CardDescription>% relativo a {classificacao.total_pedidos.toLocaleString()} pedidos</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-2 max-h-80 overflow-y-auto">
            {classificacao?.pend_motivo?.map((item) => (
              <div key={item.motivo} className="flex justify-between items-center p-2 rounded bg-muted/30">
                <span className="text-sm truncate mr-2">{item.motivo}</span>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{item.total}</Badge>
                  {classificacao?.total_pedidos > 0 && item.pct_pedidos !== undefined && (
                    <span className={`text-sm font-medium w-14 text-right ${item.pct_pedidos >= 1 ? 'text-red-600' : item.pct_pedidos >= 0.5 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {item.pct_pedidos}%
                    </span>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle className="text-lg">Top 10 Produtos (Falha Fornecedor)</CardTitle>
              <CardDescription>Ordenado por proporção chamados/vendidos</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 max-h-80 overflow-y-auto">
            {classificacao?.top_produtos?.map((item, idx) => (
              <div key={item.produto} className="flex justify-between items-center p-2 rounded bg-muted/30">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-5 h-5 shrink-0 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center">{idx + 1}</span>
                  <div className="min-w-0">
                    <span className="text-sm truncate block">{item.produto}</span>
                    {(item.sku_vtex || item.sku_bseller) && (
                      <span className="text-xs text-muted-foreground font-mono">
                        {item.sku_vtex && <span className="mr-2">VTEX: {item.sku_vtex}</span>}
                        {item.sku_bseller && <span>BSeller: {item.sku_bseller}</span>}
                      </span>
                    )}
                  </div>
                </div>
                <Badge className="shrink-0 ml-2">{item.total}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* ATENDIMENTOS x VENDAS POR FORNECEDOR (Adnéia, 27/10/2026, lista de números do Allan):
            todos os atendimentos abertos no período pela marca da entrega, sobre as entregas
            vendidas da marca no mesmo período (banco da casa). Os números vêm prontos da rota. */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Por Fornecedor / Marca: atendimentos x vendas</CardTitle>
            <CardDescription>
              {classificacao?.contato_fornecedor?.periodo_dias
                ? `Últimos ${classificacao.contato_fornecedor.periodo_dias} dias`
                : 'Todo o período (vendas: todas do tabelão)'}
              {' · '}atendimentos de todas as categorias, pela marca da entrega · vendas = entregas vendidas
              da marca no período · % = atendimentos ÷ vendas
            </CardDescription>
          </CardHeader>
          <CardContent className="max-h-80 overflow-y-auto">
            {classificacao?.contato_fornecedor?.banco_fora && (
              <p className="mb-2 text-xs text-amber-700 dark:text-amber-400" role="status">
                O banco da casa não respondeu: as vendas e o % ficam "?".
              </p>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead className="text-right">Atendimentos</TableHead>
                  <TableHead className="text-right">Vendas</TableHead>
                  <TableHead className="text-right">% sobre vendas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(classificacao?.por_fornecedor || []).map((item) => (
                  <TableRow key={item.fornecedor}>
                    <TableCell className="font-medium">{item.fornecedor}</TableCell>
                    <TableCell className="text-right">{item.total}</TableCell>
                    <TableCell className="text-right">
                      {item.vendas_periodo === null || item.vendas_periodo === undefined ? '?' : item.vendas_periodo.toLocaleString('pt-BR')}
                    </TableCell>
                    <TableCell className="text-right font-semibold"
                      title={item.vendas_periodo === 0 ? 'Sem venda da marca no período: os atendimentos são de vendas mais antigas' : undefined}>
                      {item.pct_contato === null || item.pct_contato === undefined
                        ? (item.vendas_periodo === 0 ? '—' : '?')
                        : `${item.pct_contato.toLocaleString('pt-BR')}%`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // ABA 4 - PERFORMANCE
  const TabPerformance = () => (
    <div className="space-y-6">
      {/* BONIFICAÇÃO (Adnéia, 23/09): todas as metas juntas na primeira linha. Os números
          vêm prontos do visao-geral (campo "metas") — a tela só desenha. */}
      {(visaoGeral?.metas?.length > 0) && (
        <div>
          <div className="flex items-baseline gap-2 mb-2">
            <h3 className="text-lg font-bold text-orange-800 dark:text-orange-300">Metas de bonificação</h3>
            <span className="text-sm text-muted-foreground">
              {visaoGeral.metas.filter(m => m.ok).length} de {visaoGeral.metas.length} atingidas
              {' · '}clique numa meta para ver os últimos 30 dias
            </span>
          </div>
          {/* Compacto: as 6 metas cabem numa linha. O destaque fica no título e nos
              números; meta, detalhe e status ficam pequenos de propósito. Cada caixa é um botão
              que abre o mês dela embaixo (Adnéia, 30/10/2026). */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {visaoGeral.metas.map(m => (
              <Card key={m.chave} role="button" tabIndex={0} aria-pressed={metaAberta === m.chave}
                title="Clique para ver os últimos 30 dias"
                onClick={() => setMetaAberta(a => (a === m.chave ? null : m.chave))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setMetaAberta(a => (a === m.chave ? null : m.chave));
                  }
                }}
                className={`cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 bg-orange-50/70 dark:bg-orange-950/20 border-orange-200 dark:border-orange-900 ${metaAberta === m.chave ? 'ring-2 ring-orange-400' : ''}`}>
                <CardHeader className="p-2.5 pb-1 space-y-0.5">
                  <CardTitle className="text-[13px] font-bold text-foreground leading-tight">
                    {m.rotulo}
                  </CardTitle>
                  <span className="block text-[9px] font-semibold text-orange-700 dark:text-orange-300 uppercase tracking-wide leading-tight">
                    {m.meta}
                  </span>
                </CardHeader>
                <CardContent className="p-2.5 pt-0">
                  {m.linhas ? (
                    <div className="space-y-1">
                      {m.linhas.map(l => {
                        // ETR e Tem em Casa: quem espera agora e o obtido em 30 dias (Adnéia, 03/11/2026).
                        const v = linhaDaCaixa(l);
                        return v.temObtido ? (
                          <div key={l.rotulo}>
                            <span className="block text-[11px] font-semibold text-foreground leading-tight">{l.rotulo}</span>
                            <div className="flex items-baseline justify-between gap-1">
                              <span className="text-[10px] text-muted-foreground truncate">esperando agora</span>
                              <span className={`text-sm font-bold whitespace-nowrap ${COR_DA_LINHA[v.corEsperando]}`}>
                                {v.esperando}
                              </span>
                            </div>
                            <div className="flex items-baseline justify-between gap-1"
                              title="Média, em dias úteis, dos casos que passaram por esta etapa nos últimos 30 dias">
                              <span className="text-[10px] text-muted-foreground truncate">obtido em 30 dias</span>
                              <span className={`text-sm font-bold whitespace-nowrap ${COR_DA_LINHA[v.corObtido]}`}>
                                {v.obtido}
                              </span>
                            </div>
                            <div className="text-[9px] text-muted-foreground text-right leading-none">{v.rodape}</div>
                          </div>
                        ) : (
                          <div key={l.rotulo}>
                            <div className="flex items-baseline justify-between gap-1">
                              <span className="text-[11px] text-muted-foreground truncate">{l.rotulo}</span>
                              <span className={`text-base font-bold whitespace-nowrap ${COR_DA_LINHA[v.corEsperando]}`}>
                                {v.esperando}
                              </span>
                            </div>
                            <div className="text-[9px] text-muted-foreground text-right leading-none">{l.limite}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={`text-3xl font-bold leading-none py-1 ${m.ok ? 'text-emerald-600' : 'text-red-600'}`}>
                      {m.valor}
                    </div>
                  )}
                  <p className="text-[9px] text-muted-foreground mt-1 leading-tight">{m.detalhe}</p>
                  <p className={`text-[9px] font-semibold leading-tight ${m.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                    {m.ok ? '✅ atingida' : '⚠️ abaixo'}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
          {metaAberta && <MetaHistorico chave={metaAberta} onFechar={() => setMetaAberta(null)} />}
        </div>
      )}
      {/* KPIs + graficos (movidos da Visao Geral) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* Total + Taxa de Contato */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
            <FileText className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{visaoGeral?.total || 0}</div>
            <p className="text-sm text-muted-foreground mt-1">no período</p>
            {visaoGeral?.total_pedidos > 0 && (
              <p className={`text-sm font-medium mt-1 ${getTaxaColor(visaoGeral?.taxa_contato)}`}>
                {visaoGeral?.taxa_contato}% dos pedidos
              </p>
            )}
          </CardContent>
        </Card>

        {/* Pendentes + % sobre pedidos */}
        <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => navigate('/chamados?pendente=true')}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pendentes</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{visaoGeral?.pendentes || 0}</div>
            <p className="text-sm text-muted-foreground mt-1">em aberto</p>
            {visaoGeral?.total_pedidos > 0 && (
              <p className={`text-sm font-medium mt-1 ${getTaxaColor(visaoGeral?.taxa_pendencia)}`}>
                {visaoGeral?.taxa_pendencia}% dos pedidos
              </p>
            )}
          </CardContent>
        </Card>

        <StatCard title="Resolvidos" value={visaoGeral?.resolvidos || 0} subtitle="finalizados" icon={CheckCircle} color="emerald" />
        <StatCard title="Tempo Médio" value={`${visaoGeral?.tempo_medio || 0}d`} subtitle="para resolver" icon={TrendingUp} color="blue" />
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Mais Antigo</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{visaoGeral?.dias_mais_antigo || 0}d</div>
            <p className="text-sm text-muted-foreground mt-1">em aberto</p>
          </CardContent>
        </Card>
        <StatCard title="Base ELO" value={visaoGeral?.total_pedidos?.toLocaleString() || 0} subtitle="pedidos" icon={Database} color="purple" />
      </div>

      {/* Segunda linha: indicadores proporcionais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Taxa de Resolução</CardTitle>
            <CheckCircle className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${visaoGeral?.taxa_resolucao >= 70 ? 'text-emerald-600' : visaoGeral?.taxa_resolucao >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
              {visaoGeral?.taxa_resolucao || 0}%
            </div>
            <p className="text-sm text-muted-foreground mt-1">dos atendimentos resolvidos</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">SLA ≤ 1 dia</CardTitle>
            <Gauge className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${visaoGeral?.sla_data?.em_1d >= 60 ? 'text-emerald-600' : visaoGeral?.sla_data?.em_1d >= 30 ? 'text-amber-600' : 'text-red-600'}`}>
              {visaoGeral?.sla_data?.em_1d || 0}%
            </div>
            <p className="text-sm text-muted-foreground mt-1">resolvidos em até 1 dia</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">SLA ≤ 3 dias</CardTitle>
            <Gauge className="h-4 w-4 text-indigo-500" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${visaoGeral?.sla_data?.em_3d >= 70 ? 'text-emerald-600' : visaoGeral?.sla_data?.em_3d >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
              {visaoGeral?.sla_data?.em_3d || 0}%
            </div>
            <p className="text-sm text-muted-foreground mt-1">resolvidos em até 3 dias</p>
          </CardContent>
        </Card>
        {/* 7 dias virou monitoramento (Adnéia, 23/09) — a meta passou para 15 dias. */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">SLA ≤ 7 dias</CardTitle>
            <Gauge className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${visaoGeral?.sla_data?.em_7d >= 80 ? 'text-emerald-600' : visaoGeral?.sla_data?.em_7d >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
              {visaoGeral?.sla_data?.em_7d || 0}%
            </div>
            <p className="text-sm text-muted-foreground mt-1">resolvidos em até 7 dias</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle className="text-lg">Atendimentos por Mês</CardTitle>
                <CardDescription>Volume (roxo) e % de contato — atendimentos ÷ pedidos emitidos no mês (verde)</CardDescription>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-muted-foreground">De</span>
                <select value={mesIni} onChange={(e) => fetchAtendMes(e.target.value, mesFim)}
                  className="border border-border rounded-md px-2 py-1 bg-background text-foreground">
                  {(atendMes?.meses_disponiveis || []).map(o => <option key={o.ym} value={o.ym}>{o.label}</option>)}
                </select>
                <span className="text-muted-foreground">até</span>
                <select value={mesFim} onChange={(e) => fetchAtendMes(mesIni, e.target.value)}
                  className="border border-border rounded-md px-2 py-1 bg-background text-foreground">
                  {(atendMes?.meses_disponiveis || []).map(o => <option key={o.ym} value={o.ym}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={(atendMes && Array.isArray(atendMes.por_mes) && atendMes.por_mes.length ? atendMes.por_mes : (visaoGeral?.por_mes || []))}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="%" domain={[0, 'auto']} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="total" name="Atendimentos" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6' }} />
                <Line yAxisId="right" type="monotone" dataKey="taxa_contato" name="% Contato" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Abertos vs Resolvidos</CardTitle>
            <CardDescription>Período selecionado</CardDescription>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={visaoGeral?.por_dia || []}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="data" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                <Legend />
                <Line type="monotone" dataKey="abertos" name="Abertos" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 3 }} />
                <Line type="monotone" dataKey="resolvidos" name="Resolvidos" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tempo Médio por Canal</CardTitle>
            <CardDescription>Em dias (apenas finalizados)</CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={performance?.tempo_por_canal?.slice(0, 10) || []} layout="vertical" margin={{ right: 40 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="canal" type="category" width={100} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(value) => [`${value} dias`, 'Tempo Médio']} />
                <Bar dataKey="dias" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="dias" position="right" style={{ fontSize: 11, fill: '#64748b', fontWeight: 600 }} formatter={(v) => `${v}d`} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tempo Médio por Fornecedor</CardTitle>
            <CardDescription>Em dias (apenas finalizados)</CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={performance?.tempo_por_fornecedor?.slice(0, 10) || []} layout="vertical" margin={{ right: 40 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="fornecedor" type="category" width={100} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(value) => [`${value} dias`, 'Tempo Médio']} />
                <Bar dataKey="dias" fill="#8b5cf6" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="dias" position="right" style={{ fontSize: 11, fill: '#64748b', fontWeight: 600 }} formatter={(v) => `${v}d`} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // ABA 5 - PENDÊNCIAS
  const TabPendencias = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Pendentes</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{pendencias?.total || 0}</div>
            {pendencias?.total_pedidos > 0 && (
              <p className={`text-sm font-medium mt-1 ${getTaxaColor(pendencias?.taxa_pendencia)}`}>
                {pendencias?.taxa_pendencia}% do total de pedidos
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg">Por Categoria</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-60 overflow-y-auto">
            {pendencias?.por_categoria?.map((item) => (
              <div key={item.categoria} className="flex justify-between items-center p-2 rounded bg-muted/30">
                <span className="text-sm">{item.categoria}</span>
                <Badge>{item.total}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Por Motivo</CardTitle>
            {pendencias?.total_pedidos > 0 && (
              <CardDescription>% sobre {pendencias.total_pedidos.toLocaleString()} pedidos</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-3 max-h-60 overflow-y-auto">
            {pendencias?.por_motivo?.map((item) => (
              <div key={item.motivo} className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-sm truncate mr-2">{item.motivo}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="secondary">{item.total}</Badge>
                    {pendencias?.total_pedidos > 0 && item.pct_pedidos !== undefined && (
                      <span className={`text-sm font-medium w-14 text-right ${item.pct_pedidos >= 1 ? 'text-red-600' : item.pct_pedidos >= 0.5 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {item.pct_pedidos}%
                      </span>
                    )}
                  </div>
                </div>
                {pendencias?.total_pedidos > 0 && (
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${item.pct_pedidos >= 1 ? 'bg-red-500' : item.pct_pedidos >= 0.5 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min(item.pct_pedidos * 20, 100)}%` }}
                    />
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Por Canal</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-60 overflow-y-auto">
            {pendencias?.por_canal?.map((item) => (
              <div key={item.canal} className="flex justify-between items-center p-2 rounded bg-muted/30">
                <span className="text-sm">{item.canal}</span>
                <Badge variant="outline">{item.total}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Detalhamento</CardTitle>
          <CardDescription>Lista de pendências</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Canal</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Dias</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendencias?.detalhes?.slice(0, 20).map((item) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/chamados/editar/${item.id}`)}
                  >
                    <TableCell className="font-mono text-sm">{item.id_atendimento}</TableCell>
                    <TableCell className="text-sm">{new Date(item.data_abertura).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell className="text-sm truncate max-w-32">{item.nome_cliente || '-'}</TableCell>
                    <TableCell className="text-sm">{item.parceiro || item.canal_vendas || '-'}</TableCell>
                    <TableCell><Badge variant="outline">{item.categoria}</Badge></TableCell>
                    <TableCell className="text-sm truncate max-w-32">{item.motivo_pendencia || '-'}</TableCell>
                    <TableCell className={item.dias_aberto > 3 ? 'text-red-600 font-medium' : ''}>{item.dias_aberto}d</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // ABA 6 - ESTORNOS
  const TabEstornos = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard title="Total Estornos" value={estornos?.total || 0} icon={RotateCcw} color="red" />
        {/* META (Adnéia, 22/09): estorno geral abaixo de 10%. */}
        <Card className="bg-orange-50/70 dark:bg-orange-950/20 border-orange-200 dark:border-orange-900">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              % Geral <span className="text-[11px] font-bold text-orange-700 dark:text-orange-300">· META &lt; 10%</span>
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${(estornos?.percentual_geral || 0) < 10 ? 'text-emerald-600' : 'text-red-600'}`}>
              {estornos?.percentual_geral || 0}%
            </div>
            <p className="text-sm text-muted-foreground mt-1">do total</p>
            <p className={`text-xs font-semibold mt-0.5 ${(estornos?.percentual_geral || 0) < 10 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
              {(estornos?.percentual_geral || 0) < 10 ? '✅ meta atingida' : `⚠️ ${((estornos?.percentual_geral || 0) - 10).toFixed(2)} p.p. acima da meta`}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Valor Total</CardTitle>
            <span className="text-red-500 font-bold">$</span>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {estornos?.valor_total
                ? `R$ ${estornos.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : 'R$ 0,00'}
            </div>
            <p className="text-sm text-muted-foreground mt-1">preço + frete</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">% Estornos por Mês</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={estornos?.por_mes || []}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value, name) => [name === 'percentual' ? `${value}%` : value, name === 'percentual' ? '% Estorno' : 'Estornos']} />
                <Bar dataKey="percentual" name="% Estorno" fill="#ef4444" radius={[4, 4, 0, 0]} />
                {/* Linha da meta (Adnéia, 23/09): estorno precisa ficar abaixo de 10%. */}
                <ReferenceLine y={10} stroke="#ea580c" strokeDasharray="4 4"
                  label={{ value: 'meta 10%', position: 'right', fontSize: 10, fill: '#ea580c' }} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Valor dos Estornos por Mês</CardTitle>
            <CardDescription>Preço final + frete (R$)</CardDescription>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={estornos?.valor_por_mes || []}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Valor']} />
                <Line type="monotone" dataKey="valor" name="Valor" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Ranking % por Canal</CardTitle>
          <CardDescription>Maior taxa de estorno — clique para ver chamados</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 max-h-72 overflow-y-auto">
          {estornos?.por_canal?.map((item, idx) => (
            <div key={item.canal} className="flex items-center justify-between p-2 rounded bg-muted/30 cursor-pointer hover:bg-muted/60"
              onClick={() => navigate(`/chamados?canal=${encodeURIComponent(item.canal)}&categoria=Arrependimento`)}>
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-red-100 text-red-800 text-sm flex items-center justify-center">{idx + 1}</span>
                <span className="text-sm">{item.canal}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{item.estornos}</Badge>
                <span className="text-sm font-medium text-red-600">{item.percentual}%</span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );

  // ABA 7 - REINCIDÊNCIA
  const TabReincidencia = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard title="Taxa Geral" value={`${reincidencia?.taxa_geral || 0}%`} icon={Users} color="purple" />
        <StatCard title="Clientes Reincidentes" value={reincidencia?.total_reincidentes || 0} icon={RotateCcw} color="orange" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Reincidência por Canal</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-80 overflow-y-auto">
            {reincidencia?.por_canal?.map((item) => (
              <div key={item.canal} className="flex justify-between items-center p-2 rounded bg-muted/30">
                <span className="text-sm">{item.canal}</span>
                <Badge variant="secondary">{item.reincidentes}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Reincidência por Produto</CardTitle>
            <CardDescription>Top 10 produtos com mais reincidências</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 max-h-80 overflow-y-auto">
            {reincidencia?.por_produto?.map((item, idx) => (
              <div key={item.produto} className="flex justify-between items-center p-2 rounded bg-muted/30">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-800 text-sm flex items-center justify-center">{idx + 1}</span>
                  <span className="text-sm truncate max-w-xs">{item.produto}</span>
                </div>
                <Badge>{item.reincidentes}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // ABA: OUTRAS ATIVIDADES
  const TabAtividades = () => {
    const retirada = retiradaData || [];
    const pagamento = pagamentoData || [];

    const retPendentes = retirada.filter(p => !p.status_final);
    const retTratados  = retirada.filter(p => !!p.status_final);
    const pagPendentes = pagamento.filter(p => !p.status_final);
    const pagTratados  = pagamento.filter(p => !!p.status_final);
    const pagUrgentes  = pagPendentes.filter(p => (p.dias_no_status ?? 0) >= 7);

    if (loadingAtividades) {
      return (
        <div className="flex items-center justify-center py-20 text-muted-foreground gap-2">
          <RefreshCw className="h-5 w-5 animate-spin" /> Carregando atividades...
        </div>
      );
    }

    const SectionHeader = ({ icon: Icon, label, color, pendentes, tratados }) => (
      <div className={`flex items-center justify-between px-4 py-3 rounded-lg border ${color}`}>
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5" />
          <span className="font-semibold text-base">{label}</span>
        </div>
        <div className="flex gap-4 text-sm">
          <span className="text-amber-600 font-medium">{pendentes} pendentes</span>
          <span className="text-emerald-600 font-medium">{tratados} tratados</span>
        </div>
      </div>
    );

    return (
      <div className="space-y-8">

        {/* ── DISPONÍVEL PARA RETIRADA ── */}
        <div className="space-y-3">
          <SectionHeader
            icon={Package}
            label="Disponível para Retirada"
            color="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200"
            pendentes={retPendentes.length}
            tratados={retTratados.length}
          />

          {/* Pendentes Retirada */}
          {retPendentes.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-amber-600 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4" /> Necessário Tratar ({retPendentes.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-amber-50 dark:bg-amber-950/20 border-b border-amber-100">
                      <tr>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-amber-700">Nota</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-amber-700">Canal</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-amber-700">Cliente</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-amber-700">Produto</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-amber-700">Acionamento</th>
                        <th className="text-right px-4 py-2 text-sm font-semibold text-amber-700">Dias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {retPendentes.map(p => {
                        const dias = p.dias_disponivel ?? '—';
                        const urgente = typeof dias === 'number' && dias >= 7;
                        return (
                          <tr key={p.nota_fiscal} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                            onClick={() => navigate('/retirada')}>
                            <td className="px-4 py-2 font-mono text-sm font-semibold">{p.nota_fiscal}</td>
                            <td className="px-4 py-2 text-sm">{p.canal_vendas || '—'}</td>
                            <td className="px-4 py-2 text-sm max-w-[140px] truncate">{p.nome_cliente || '—'}</td>
                            <td className="px-4 py-2 text-sm max-w-[160px] truncate">{p.produto || '—'}</td>
                            <td className="px-4 py-2 text-sm">{p.acionamento || '—'}</td>
                            <td className="px-4 py-2 text-right">
                              <span className={`text-sm font-bold ${urgente ? 'text-red-600' : 'text-amber-600'}`}>
                                {dias}{typeof dias === 'number' ? 'd' : ''}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tratados Retirada */}
          {retTratados.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-emerald-600 flex items-center gap-1.5">
                  <CheckCircle className="h-4 w-4" /> Tratados ({retTratados.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-emerald-50 dark:bg-emerald-950/20 border-b border-emerald-100">
                      <tr>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-emerald-700">Nota</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-emerald-700">Canal</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-emerald-700">Cliente</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-emerald-700">Status Final</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-emerald-700">Finalizado por</th>
                      </tr>
                    </thead>
                    <tbody>
                      {retTratados.map(p => (
                        <tr key={p.nota_fiscal} className="border-b last:border-0 bg-emerald-50/30 dark:bg-emerald-950/10 cursor-pointer hover:bg-emerald-50"
                          onClick={() => navigate('/retirada')}>
                          <td className="px-4 py-2 font-mono text-sm font-semibold">{p.nota_fiscal}</td>
                          <td className="px-4 py-2 text-sm">{p.canal_vendas || '—'}</td>
                          <td className="px-4 py-2 text-sm max-w-[140px] truncate">{p.nome_cliente || '—'}</td>
                          <td className="px-4 py-2 text-sm">
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-sm">{p.status_final}</Badge>
                          </td>
                          <td className="px-4 py-2 text-sm text-slate-500">{p.finalizado_por || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {retirada.length === 0 && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              Nenhum pedido disponível para retirada
            </div>
          )}
        </div>

        {/* ── PAGAMENTO NÃO APROVADO ── */}
        <div className="space-y-3">
          <SectionHeader
            icon={FileText}
            label="Pagamento Não Aprovado"
            color="bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200"
            pendentes={pagPendentes.length}
            tratados={pagTratados.length}
          />

          {/* Pendentes Pagamento */}
          {pagPendentes.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-amber-600 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4" />
                  Necessário Tratar ({pagPendentes.length})
                  {pagUrgentes.length > 0 && (
                    <Badge className="ml-2 bg-red-100 text-red-700 border-red-200 text-sm">
                      ⚠ {pagUrgentes.length} urgentes (+7 dias)
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-amber-50 dark:bg-amber-950/20 border-b border-amber-100">
                      <tr>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-amber-700">Pedido</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-amber-700">Canal</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-amber-700">Cliente</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-amber-700">CPF</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-amber-700">Produto</th>
                        <th className="text-right px-4 py-2 text-sm font-semibold text-amber-700">Dias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagPendentes.map(p => {
                        const urgente = (p.dias_no_status ?? 0) >= 7;
                        return (
                          <tr key={p.numero_pedido}
                            className={`border-b last:border-0 cursor-pointer hover:bg-muted/30 ${urgente ? 'bg-red-50/40 dark:bg-red-950/10' : ''}`}
                            onClick={() => navigate('/pagamento')}>
                            <td className="px-4 py-2 font-mono text-sm font-semibold">{p.numero_pedido}</td>
                            <td className="px-4 py-2 text-sm">{p.canal_vendas || '—'}</td>
                            <td className="px-4 py-2 text-sm max-w-[140px] truncate">{p.nome_cliente || '—'}</td>
                            <td className="px-4 py-2 font-mono text-sm">{p.cpf_cliente || '—'}</td>
                            <td className="px-4 py-2 text-sm max-w-[160px] truncate">{p.produto || '—'}</td>
                            <td className="px-4 py-2 text-right">
                              <span className={`text-sm font-bold ${urgente ? 'text-red-600' : 'text-amber-600'}`}>
                                {p.dias_no_status ?? '—'}{p.dias_no_status != null ? 'd' : ''}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tratados Pagamento */}
          {pagTratados.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-emerald-600 flex items-center gap-1.5">
                  <CheckCircle className="h-4 w-4" /> Tratados — Cancelados ({pagTratados.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-emerald-50 dark:bg-emerald-950/20 border-b border-emerald-100">
                      <tr>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-emerald-700">Pedido</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-emerald-700">Canal</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-emerald-700">Cliente</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-emerald-700">Instância</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-emerald-700">Data</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold text-emerald-700">Registrado por</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagTratados.map(p => (
                        <tr key={p.numero_pedido}
                          className="border-b last:border-0 bg-emerald-50/30 dark:bg-emerald-950/10 cursor-pointer hover:bg-emerald-50"
                          onClick={() => navigate('/pagamento')}>
                          <td className="px-4 py-2 font-mono text-sm font-semibold">{p.numero_pedido}</td>
                          <td className="px-4 py-2 text-sm">{p.canal_vendas || '—'}</td>
                          <td className="px-4 py-2 text-sm max-w-[140px] truncate">{p.nome_cliente || '—'}</td>
                          <td className="px-4 py-2 font-mono text-sm font-semibold text-emerald-700">{p.instancia || '—'}</td>
                          <td className="px-4 py-2 text-sm text-slate-500">{p.data_instancia || '—'}</td>
                          <td className="px-4 py-2 text-sm text-slate-500">{p.registrado_por || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {pagamento.length === 0 && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              Nenhum pedido com pagamento não aprovado
            </div>
          )}
        </div>

      </div>
    );
  };

  if (loading && !visaoGeral) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2"><Skeleton className="h-4 w-20" /></CardHeader>
              <CardContent><Skeleton className="h-8 w-16" /></CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Finalizar Dia: rodízio (o que ficou aberto da pessoa + recado do colega) */}
      <FinalizarDiaDialog open={showFinalizarDialog} onOpenChange={setShowFinalizarDialog} />
      {/* Header com Filtros Globais */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Plus_Jakarta_Sans']">Dashboard ELO</h1>
          <p className="text-sm text-muted-foreground">
            {lastUpdated && `Atualizado: ${lastUpdated.toLocaleTimeString('pt-BR')}`}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <Button
            variant="outline"
            onClick={() => setShowFinalizarDialog(true)}
            className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            data-testid="btn-finalizar-dia"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Finalizar Dia
          </Button>
          <Select value={String(periodoDias)} onValueChange={(v) => setPeriodoDias(Number(v))}>
            <SelectTrigger className="w-28">
              <Calendar className="h-4 w-4 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 dias</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
              <SelectItem value="90">90 dias</SelectItem>
              <SelectItem value="365">1 ano</SelectItem>
            </SelectContent>
          </Select>

          <Select value={canalFiltro || "all"} onValueChange={(v) => setCanalFiltro(v === "all" ? "" : v)}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Canal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Canais</SelectItem>
              {filtros.canais.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={fornecedorFiltro || "all"} onValueChange={(v) => setFornecedorFiltro(v === "all" ? "" : v)}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Fornecedor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Fornec.</SelectItem>
              {filtros.fornecedores.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon" onClick={() => { if (activeTab !== 'acessos') fetchTabData(activeTab); }}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid grid-cols-4 ${veAcessos ? 'lg:grid-cols-9' : 'lg:grid-cols-7'} w-full`}>
          <TabsTrigger value="visao-geral" className="gap-1">
            <LayoutGrid className="h-4 w-4 hidden sm:block" /> Visão Geral
          </TabsTrigger>
          {/* KPIs (Adnéia, 28/10/2026): os números do Allan, vivos; só o atendimento vê. */}
          {veAcessos && (
            <TabsTrigger value="kpis" className="gap-1">
              <TrendingUp className="h-4 w-4 hidden sm:block" /> KPIs
            </TabsTrigger>
          )}
          <TabsTrigger value="performance" className="gap-1">
            <Gauge className="h-4 w-4 hidden sm:block" /> Performance
          </TabsTrigger>
          <TabsTrigger value="volume-canal" className="gap-1">
            <BarChart3 className="h-4 w-4 hidden sm:block" /> Volume
          </TabsTrigger>
          <TabsTrigger value="classificacao" className="gap-1">
            <Tag className="h-4 w-4 hidden sm:block" /> Classificação
          </TabsTrigger>
          <TabsTrigger value="pendencias" className="gap-1">
            <ListChecks className="h-4 w-4 hidden sm:block" /> Pendências
          </TabsTrigger>
          <TabsTrigger value="estornos" className="gap-1">
            <RotateCcw className="h-4 w-4 hidden sm:block" /> Estornos
          </TabsTrigger>
          <TabsTrigger value="reincidencia" className="gap-1">
            <Users className="h-4 w-4 hidden sm:block" /> Reincidência
          </TabsTrigger>
          {veAcessos && (
            <TabsTrigger value="acessos" className="gap-1">
              <KeyRound className="h-4 w-4 hidden sm:block" /> Acessos
            </TabsTrigger>
          )}
        </TabsList>

        {/* Renderizado como função inline (não <TabVisaoGeral/>) para não remontar a
            aba a cada re-render — senão os inputs (ex.: Observação) perdem o foco ao digitar. */}
        <TabsContent value="visao-geral" className="mt-6">{TabVisaoGeral()}</TabsContent>
        <TabsContent value="volume-canal" className="mt-6"><TabVolumeCanal /></TabsContent>
        <TabsContent value="classificacao" className="mt-6"><TabClassificacao /></TabsContent>
        <TabsContent value="performance" className="mt-6"><TabPerformance /></TabsContent>
        <TabsContent value="pendencias" className="mt-6"><TabPendencias /></TabsContent>
        <TabsContent value="estornos" className="mt-6"><TabEstornos /></TabsContent>
        <TabsContent value="reincidencia" className="mt-6"><TabReincidencia /></TabsContent>
        {veAcessos && <TabsContent value="acessos" className="mt-6"><AcessosCanais /></TabsContent>}
        {veAcessos && <TabsContent value="kpis" className="mt-6"><AbaKpis /></TabsContent>}
      </Tabs>
      <ListaCobranca grupo={cobranca} onClose={() => setCobranca(null)} />
      <HistoricoVerificacao alvo={histAlvo} onClose={() => setHistAlvo(null)} />
      <CheckCanalDialog
        canal={checkCanalAberto}
        lista={checklists[checkCanalAberto]}
        atual={canalChecks[checkCanalAberto] || null}
        onClose={() => setCheckCanalAberto('')}
        onSalvo={(data) => {
          aplicarCheck(checkCanalAberto, data);
          // Atividade: o servidor devolve o carimbo do Feito hoje (verde carimba, amarelo tira).
          const rot = checklists[checkCanalAberto]?.rotulo;
          if (data.verificacao && rot) {
            updateAtivLocal(rot, { ultima: data.verificacao.ultima || '', proxima: data.verificacao.proxima || '',
              check_por: data.verificacao.check_por || '', check_em: data.verificacao.check_em || '' });
          }
        }}
      />
      <RespostasCanal canal={respostasCanal} lista={checklists[respostasCanal]} onClose={() => setRespostasCanal('')} />
      <ManualAtividade nome={manualAberto} manual={manuais[manualAberto]} onClose={() => setManualAberto('')} />
      <NovidadesDashboard />
    </div>
  );
};

export default Dashboard;
